// Blog article translations - Batch 3 (articles 11-15)
// This file contains translated content for 5 additional blog articles

export const blogArticleTranslationsBatch3 = {
  en: {
    // Article 11: high-resolution-photos-portals (2020-02-10)
    'blog.article.photo-portals.title': 'High-Resolution Photos and Tags Now Sent to OTAs',
    'blog.article.photo-portals.excerpt': 'Your property photos are now published in high resolution with descriptive tags to Booking.com, Airbnb, and other major portals.',
    'blog.article.photo-portals.content': `
## Better Photos on Every Platform

We've upgraded how i-Rent sends photos to external portals. Your high-resolution images are now delivered in full quality, along with descriptive tags that help platforms categorize and display your photos effectively.

## Photo Tags by Platform

Different portals support photo tagging in different ways:

- **Booking.com** — Supports multiple tags per photo
- **Airbnb** — Supports one tag per photo
- **HomeAway** — Supports one tag per photo with multiple language translations

## Why Photo Tags Matter

Photo tags help booking platforms understand what each image shows — whether it's a bedroom, bathroom, pool, or view. This improves:

- **Search visibility** — Platforms can match your photos to guest searches
- **Guest experience** — Travelers find what they're looking for faster
- **Listing quality** — Well-tagged photos signal a professional listing

## High Resolution Everywhere

Along with tags, your photos are now sent in high resolution to portals that support it. This means:

- **Sharper images** — No more compression artifacts
- **Better first impressions** — High-quality photos attract more clicks
- **Professional presentation** — Your properties look their best

## Get Started

If you haven't tried our updated photo system yet, now is the perfect time. Take advantage of high-resolution uploads and photo tagging to make your listings stand out on every platform.
    `,

    // Article 12: discounts-sync-airbnb (2020-02-25)
    'blog.article.airbnb-discounts.title': 'Simple Discounts Now Sync to Airbnb',
    'blog.article.airbnb-discounts.excerpt': 'Your percentage-based discounts created in i-Rent are now automatically sent to Airbnb — helping you attract more bookings with consistent promotions.',
    'blog.article.airbnb-discounts.content': `
## Discounts on Airbnb, Managed from i-Rent

We've expanded our Airbnb integration to include discount synchronization. Simple discounts you create in i-Rent are now automatically sent to your Airbnb listings.

## Requirements for Airbnb Discounts

For a discount to sync to Airbnb, it must meet these conditions:

- **Percentage-based** — Only discounts using a percentage (%) are supported
- **No maximum stay** — The discount cannot have a maximum stay restriction

## How Discounts Are Applied

When sending discounts to Airbnb, i-Rent applies the following rules:

### Date Settings
- **Publishing begin/end dates** — If set, the discount only appears during that period
- **Arrival date range** — If specified, the discount applies to bookings arriving within those dates; if left empty, the discount applies to all days with pricing

### Stay Requirements
- **Minimum stay** — If you set a minimum stay, the discount only applies when it's equal to or less than the accommodation's minimum stay requirement
- **Maximum stay** — Not supported for Airbnb discounts

### Calculation Method
- **Applying criteria** — The discount applies if at least one day of the booking falls within the discount period
- **Calculation** — The discount is calculated only for days that fall within the discount period

## Benefits

- **Consistent promotions** — Manage all your discounts from one place
- **Automatic sync** — No need to manually update Airbnb
- **More bookings** — Attractive discounts help fill your calendar
- **Full control** — Set exactly when and how discounts apply

## Start Creating Discounts

Create your percentage-based discounts in i-Rent and watch them sync automatically to Airbnb. It's a simple way to boost your visibility and attract more guests.
    `,

    // Article 13: accommodation-categories-rental-types (2020-03-10)
    'blog.article.rental-categories.title': 'Categorize Accommodations by Rental Type',
    'blog.article.rental-categories.excerpt': 'Organize your portfolio by distinguishing between holiday rentals, long-term rentals, and maintenance-only properties — streamlining your publishing workflow.',
    'blog.article.rental-categories.content': `
## Better Organization for Your Properties

We've added a new feature that helps you organize your accommodations more effectively by categorizing them based on their purpose.

## Three Property Categories

You can now assign each accommodation to one of three categories:

- **Holiday rentals** — Properties available for short-term holiday bookings
- **Long-term rentals** — Properties intended for extended stays or residential leases
- **Maintenance and service only** — Properties not available for guest bookings

## How to Set Up

Navigate to the **Accommodation Information** menu in i-Rent and select the appropriate category for each property. The setting is quick to configure and takes effect immediately.

## Smarter Publishing

The real benefit comes when publishing to websites and portals:

- **Maintenance-only properties are automatically excluded** from publishing lists
- **No more accidental listings** of properties that shouldn't be bookable
- **Bulk publishing becomes safer** — select groups of accommodations without worrying about including the wrong properties

## Benefits

- **Cleaner workflow** — Focus only on properties that should be published
- **Fewer errors** — Prevent maintenance properties from appearing on booking sites
- **Better organization** — Clear separation between different rental types
- **Time savings** — No need to manually exclude properties when publishing

## Start Organizing

Take a few minutes to categorize your accommodations. This small investment of time will make your day-to-day operations smoother and reduce the risk of publishing mistakes.
    `,

    // Article 14: holidu-online-payment-link (2020-07-15)
    'blog.article.holidu-payments.title': 'Online Payment Links Now Displayed on Holidu',
    'blog.article.holidu-payments.excerpt': 'Holidu bookings now include automatic payment links — guests see the payment option directly on the confirmation page and in their email.',
    'blog.article.holidu-payments.content': `
## Seamless Payments for Holidu Bookings

We've integrated automatic payment links for bookings made through Holidu. This streamlines the payment process and improves the guest experience.

## How It Works

When Holidu creates a booking, i-Rent automatically sends back an online payment link. This link is then:

- **Displayed on Holidu's booking confirmation page** — guests can pay immediately after booking
- **Included in the confirmation email** — easy access for guests who prefer to pay later

The payment link includes the rental amount plus any mandatory extras that need to be paid at the time of booking.

## Requirements

This feature is available for property managers who have a **POS (Point of Sale) system** configured in i-Rent. If you haven't set up your payment system yet, contact our support team for assistance.

## Benefits

- **Faster payments** — Guests can pay immediately without waiting for manual invoices
- **Better conversion** — Reducing friction leads to more completed bookings
- **Professional experience** — Branded payment pages build trust with guests
- **Automatic tracking** — All payments sync directly to your i-Rent dashboard

## Get Started

If you're already connected to Holidu with a POS system in i-Rent, this feature is active automatically. Start enjoying smoother payment flows for your Holidu bookings today.
    `,

    // Article 15: better-photos-holiday-homes (2021-04-15)
    'blog.article.photo-tips.title': 'How to Take Better Pictures of Holiday Homes',
    'blog.article.photo-tips.excerpt': 'Professional photography tips for holiday rentals — from exterior shots to interior staging, learn how to capture images that convert browsers into bookers.',
    'blog.article.photo-tips.content': `
## Why Great Photos Matter

Quality photography is one of the most important factors in attracting bookings. These professional tips will help you capture images that showcase your properties at their best.

## Camera Basics

Before you start shooting, keep these fundamentals in mind:

- **Always shoot in landscape format** to avoid cropping issues on OTAs and portals
- **Use maximum resolution** for the sharpest possible images
- **Keep the camera level** (about 1.5m from the ground) to avoid skewed walls and doors
- **Use a wide-angle lens** to capture more of each space
- **Take day shots plus 1-2 evening shots** just after sunset when the sky is still blue

## Outdoor Photography

### General Guidelines

1. **Only shoot on sunny days** — small white clouds are acceptable
2. **Position the sun behind you** for blue skies and even lighting
3. **Open all blinds, doors, and curtains** visible from outside
4. **Minimize neighboring buildings** in your frame — avoid showing windows of surrounding houses

### Garden & Pool

- Prepare the area exactly as guests would find it on arrival
- Arrange sun loungers and furniture in an orderly, inviting way
- Add towels on loungers and use tasteful decoration
- Hide garden hoses and messy items
- **Pro tip:** Stir the pool water with a broom before shooting to hide any debris on the bottom

### Terraces

- Keep everything clean and tidy
- Set the table completely with plates, cutlery, wine, and fruit
- Create an atmosphere that invites dining

### Views

- Minimize surrounding buildings in the frame
- Shoot with the sun behind you
- Show that the view belongs to this specific property

## Indoor Photography

### General Indoor Tips

- **Turn on ALL lights** — every single one
- **Tidy thoroughly** and remove clutter
- **Shoot from corners** with no more than 2 walls visible — this creates better spatial feeling
- **Rearrange furniture** to create more space; avoid photographing backs of sofas
- **Hide cables** and electrical wires along walls
- **Straighten cushions** or remove them for cleaner compositions
- Use decoration, but don't overdo it

### Managing Light

- If too much sunlight enters through windows, close curtains or cover windows from outside
- For shots through windows to a bright exterior, use a tripod and take two exposures — one for the view, one for the interior — then combine in Photoshop

### Living & Dining Rooms

- Remove carpets, sofa covers, and excess cushions
- Remove personal items like family photos
- Open windows and doors when light permits
- Capture interesting detail shots

### Bedrooms

- Use only white sheets or duvets
- Add rolled towels on the bed and multiple pillows
- Remove all personal items
- In small rooms, focus on the bed detail with nightstands

### Bathrooms

- Remove all personal items
- If space is too tight for landscape orientation, take two portrait shots and combine them side by side

## Post-Processing Tips

- Crop away any distracting elements
- Straighten walls and doors in your photo editor
- Brighten images and sharpen where necessary
- Ensure consistent quality across all photos

## Start Shooting

Great photography doesn't require expensive equipment — just attention to detail and proper technique. Apply these tips consistently and watch your booking inquiries increase.
    `,
  },

  nl: {
    // Article 11: high-resolution-photos-portals
    'blog.article.photo-portals.title': "Foto's in Hoge Resolutie en Tags Nu Verzonden naar OTA's",
    'blog.article.photo-portals.excerpt': "Uw vastgoedfoto's worden nu gepubliceerd in hoge resolutie met beschrijvende tags naar Booking.com, Airbnb en andere grote portals.",
    'blog.article.photo-portals.content': `
## Betere Foto's op Elk Platform

We hebben geüpgraded hoe i-Rent foto's naar externe portals verstuurt. Uw hoge resolutie afbeeldingen worden nu geleverd in volledige kwaliteit, samen met beschrijvende tags die platforms helpen uw foto's effectief te categoriseren en weer te geven.

## Foto Tags per Platform

Verschillende portals ondersteunen foto-tagging op verschillende manieren:

- **Booking.com** — Ondersteunt meerdere tags per foto
- **Airbnb** — Ondersteunt één tag per foto
- **HomeAway** — Ondersteunt één tag per foto met meerdere taalvertalingen

## Waarom Foto Tags Belangrijk Zijn

Foto tags helpen boekingsplatforms te begrijpen wat elke afbeelding toont — of het nu een slaapkamer, badkamer, zwembad of uitzicht is. Dit verbetert:

- **Zoekzichtbaarheid** — Platforms kunnen uw foto's matchen met zoekopdrachten van gasten
- **Gastervaring** — Reizigers vinden sneller wat ze zoeken
- **Vermeldingskwaliteit** — Goed getagde foto's signaleren een professionele vermelding

## Hoge Resolutie Overal

Samen met tags worden uw foto's nu in hoge resolutie verzonden naar portals die dit ondersteunen. Dit betekent:

- **Scherpere afbeeldingen** — Geen compressie-artefacten meer
- **Betere eerste indrukken** — Hoogwaardige foto's trekken meer klikken aan
- **Professionele presentatie** — Uw accommodaties zien er op hun best uit

## Aan de Slag

Als u ons bijgewerkte fotosysteem nog niet hebt geprobeerd, is nu het perfecte moment. Profiteer van uploads in hoge resolutie en foto-tagging om uw vermeldingen te laten opvallen op elk platform.
    `,

    // Article 12: discounts-sync-airbnb
    'blog.article.airbnb-discounts.title': 'Eenvoudige Kortingen Synchroniseren Nu met Airbnb',
    'blog.article.airbnb-discounts.excerpt': 'Uw op percentage gebaseerde kortingen gemaakt in i-Rent worden nu automatisch verzonden naar Airbnb — waardoor u meer boekingen aantrekt met consistente promoties.',
    'blog.article.airbnb-discounts.content': `
## Kortingen op Airbnb, Beheerd vanuit i-Rent

We hebben onze Airbnb-integratie uitgebreid met kortingssynchronisatie. Eenvoudige kortingen die u in i-Rent maakt, worden nu automatisch verzonden naar uw Airbnb-vermeldingen.

## Vereisten voor Airbnb Kortingen

Om een korting te synchroniseren met Airbnb, moet deze aan deze voorwaarden voldoen:

- **Op percentage gebaseerd** — Alleen kortingen met een percentage (%) worden ondersteund
- **Geen maximale verblijfsduur** — De korting mag geen beperking op maximale verblijfsduur hebben

## Hoe Kortingen Worden Toegepast

Bij het verzenden van kortingen naar Airbnb past i-Rent de volgende regels toe:

### Datuminstellingen
- **Begin-/einddatum publicatie** — Indien ingesteld, verschijnt de korting alleen gedurende die periode
- **Aankomstdatumbereik** — Indien gespecificeerd, geldt de korting voor boekingen die binnen die datums aankomen; indien leeg gelaten, geldt de korting voor alle dagen met prijzen

### Verblijfsvereisten
- **Minimaal verblijf** — Als u een minimaal verblijf instelt, geldt de korting alleen wanneer deze gelijk is aan of minder dan de minimale verblijfsvereiste van de accommodatie
- **Maximaal verblijf** — Niet ondersteund voor Airbnb-kortingen

### Berekeningsmethode
- **Toepassingscriteria** — De korting geldt als ten minste één dag van de boeking binnen de kortingsperiode valt
- **Berekening** — De korting wordt alleen berekend voor dagen die binnen de kortingsperiode vallen

## Voordelen

- **Consistente promoties** — Beheer al uw kortingen vanaf één plek
- **Automatische synchronisatie** — Geen handmatige updates van Airbnb nodig
- **Meer boekingen** — Aantrekkelijke kortingen helpen uw kalender te vullen
- **Volledige controle** — Bepaal precies wanneer en hoe kortingen van toepassing zijn

## Begin met Kortingen Maken

Maak uw op percentage gebaseerde kortingen in i-Rent en zie ze automatisch synchroniseren met Airbnb. Het is een eenvoudige manier om uw zichtbaarheid te vergroten en meer gasten aan te trekken.
    `,

    // Article 13: accommodation-categories-rental-types
    'blog.article.rental-categories.title': 'Categoriseer Accommodaties op Verhuurtype',
    'blog.article.rental-categories.excerpt': 'Organiseer uw portfolio door onderscheid te maken tussen vakantieverhuur, langetermijnverhuur en alleen-onderhoud accommodaties — stroomlijn uw publicatieworkflow.',
    'blog.article.rental-categories.content': `
## Betere Organisatie voor Uw Accommodaties

We hebben een nieuwe functie toegevoegd die u helpt uw accommodaties effectiever te organiseren door ze te categoriseren op basis van hun doel.

## Drie Accommodatiecategorieën

U kunt nu elke accommodatie toewijzen aan een van drie categorieën:

- **Vakantieverhuur** — Accommodaties beschikbaar voor kortetermijn vakantie boekingen
- **Langetermijnverhuur** — Accommodaties bedoeld voor verlengde verblijven of residentiële huur
- **Alleen onderhoud en service** — Accommodaties niet beschikbaar voor gastboekingen

## Hoe In te Stellen

Navigeer naar het **Accommodatie-informatie** menu in i-Rent en selecteer de juiste categorie voor elke accommodatie. De instelling is snel te configureren en wordt onmiddellijk van kracht.

## Slimmer Publiceren

Het echte voordeel komt bij het publiceren naar websites en portals:

- **Alleen-onderhoud accommodaties worden automatisch uitgesloten** van publicatielijsten
- **Geen onbedoelde vermeldingen meer** van accommodaties die niet boekbaar zouden moeten zijn
- **Bulk publiceren wordt veiliger** — selecteer groepen accommodaties zonder je zorgen te maken over het opnemen van de verkeerde accommodaties

## Voordelen

- **Schonere workflow** — Focus alleen op accommodaties die gepubliceerd moeten worden
- **Minder fouten** — Voorkom dat onderhoudsaccommodaties op boekingssites verschijnen
- **Betere organisatie** — Duidelijke scheiding tussen verschillende verhuurtypen
- **Tijdsbesparing** — Niet handmatig accommodaties uitsluiten bij publiceren

## Begin met Organiseren

Neem een paar minuten om uw accommodaties te categoriseren. Deze kleine investering van tijd maakt uw dagelijkse activiteiten soepeler en vermindert het risico op publicatiefouten.
    `,

    // Article 14: holidu-online-payment-link
    'blog.article.holidu-payments.title': 'Online Betalingslinks Nu Weergegeven op Holidu',
    'blog.article.holidu-payments.excerpt': 'Holidu-boekingen bevatten nu automatische betalingslinks — gasten zien de betaaloptie direct op de bevestigingspagina en in hun e-mail.',
    'blog.article.holidu-payments.content': `
## Naadloze Betalingen voor Holidu Boekingen

We hebben automatische betalingslinks geïntegreerd voor boekingen gemaakt via Holidu. Dit stroomlijnt het betalingsproces en verbetert de gastervaring.

## Hoe Het Werkt

Wanneer Holidu een boeking maakt, stuurt i-Rent automatisch een online betalingslink terug. Deze link wordt dan:

- **Weergegeven op Holidu's boekingsbevestigingspagina** — gasten kunnen direct na het boeken betalen
- **Opgenomen in de bevestigingsmail** — gemakkelijke toegang voor gasten die liever later betalen

De betalingslink bevat het huurbedrag plus eventuele verplichte extra's die bij het boeken betaald moeten worden.

## Vereisten

Deze functie is beschikbaar voor vastgoedbeheerders die een **POS (Point of Sale) systeem** geconfigureerd hebben in i-Rent. Als u uw betalingssysteem nog niet hebt ingesteld, neem dan contact op met ons supportteam voor hulp.

## Voordelen

- **Snellere betalingen** — Gasten kunnen direct betalen zonder te wachten op handmatige facturen
- **Betere conversie** — Minder wrijving leidt tot meer voltooide boekingen
- **Professionele ervaring** — Merkbetalingspagina's bouwen vertrouwen op bij gasten
- **Automatische tracking** — Alle betalingen synchroniseren direct naar uw i-Rent dashboard

## Aan de Slag

Als u al verbonden bent met Holidu met een POS-systeem in i-Rent, is deze functie automatisch actief. Begin vandaag nog te genieten van soepelere betalingsstromen voor uw Holidu-boekingen.
    `,

    // Article 15: better-photos-holiday-homes
    'blog.article.photo-tips.title': "Hoe Betere Foto's te Maken van Vakantiewoningen",
    'blog.article.photo-tips.excerpt': "Professionele fotografietips voor vakantieverhuur — van buitenopnames tot interieur styling, leer hoe u beelden vastlegt die browsers omzetten in boekingen.",
    'blog.article.photo-tips.content': `
## Waarom Geweldige Foto's Belangrijk Zijn

Kwaliteitsfotografie is een van de belangrijkste factoren bij het aantrekken van boekingen. Deze professionele tips helpen u afbeeldingen vast te leggen die uw accommodaties op hun best laten zien.

## Camera Basisprincipes

Houd deze fundamenten in gedachten voordat u begint met fotograferen:

- **Fotografeer altijd in landschapsformaat** om bijsnijproblemen op OTA's en portals te voorkomen
- **Gebruik maximale resolutie** voor de scherpste afbeeldingen
- **Houd de camera waterpas** (ongeveer 1,5m van de grond) om scheve muren en deuren te voorkomen
- **Gebruik een groothoeklens** om meer van elke ruimte vast te leggen
- **Maak dagopnames plus 1-2 avondopnames** net na zonsondergang wanneer de lucht nog blauw is

## Buitenfotografie

### Algemene Richtlijnen

1. **Fotografeer alleen op zonnige dagen** — kleine witte wolken zijn acceptabel
2. **Positioneer de zon achter u** voor blauwe luchten en gelijkmatige belichting
3. **Open alle jaloezieën, deuren en gordijnen** die van buiten zichtbaar zijn
4. **Minimaliseer naburige gebouwen** in uw beeld — vermijd het tonen van ramen van omliggende huizen

### Tuin & Zwembad

- Bereid het gebied precies voor zoals gasten het bij aankomst zouden aantreffen
- Rangschik ligbedden en meubilair op een ordelijke, uitnodigende manier
- Voeg handdoeken toe op ligbedden en gebruik smaakvolle decoratie
- Verberg tuinslangen en rommelige items
- **Pro tip:** Roer het zwembadwater met een bezem vóór het fotograferen om vuil op de bodem te verbergen

### Terrassen

- Houd alles schoon en netjes
- Dek de tafel volledig met borden, bestek, wijn en fruit
- Creëer een sfeer die uitnodigt tot dineren

### Uitzichten

- Minimaliseer omliggende gebouwen in het beeld
- Fotografeer met de zon achter u
- Laat zien dat het uitzicht bij deze specifieke accommodatie hoort

## Binnenfotografie

### Algemene Binnentips

- **Zet ALLE lichten aan** — elk afzonderlijk
- **Ruim grondig op** en verwijder rommel
- **Fotografeer vanuit hoeken** met niet meer dan 2 muren zichtbaar — dit creëert een beter ruimtegevoel
- **Herschik meubilair** om meer ruimte te creëren; vermijd het fotograferen van de achterkant van banken
- **Verberg kabels** en elektrische draden langs muren
- **Strijk kussens glad** of verwijder ze voor schonere composities
- Gebruik decoratie, maar overdrijf niet

### Lichtbeheer

- Als er te veel zonlicht door ramen binnenkomt, sluit gordijnen of bedek ramen van buiten
- Voor opnames door ramen naar een helder exterieur, gebruik een statief en maak twee belichtingen — één voor het uitzicht, één voor het interieur — combineer ze vervolgens in Photoshop

### Woon- & Eetkamers

- Verwijder tapijten, bankhoezen en overtollige kussens
- Verwijder persoonlijke items zoals familiefoto's
- Open ramen en deuren wanneer het licht het toelaat
- Leg interessante detailopnames vast

### Slaapkamers

- Gebruik alleen witte lakens of dekbedden
- Voeg opgerolde handdoeken toe op het bed en meerdere kussens
- Verwijder alle persoonlijke items
- In kleine kamers, focus op het beddetail met nachtkastjes

### Badkamers

- Verwijder alle persoonlijke items
- Als de ruimte te krap is voor landschapsoriëntatie, maak twee portretopnames en combineer ze naast elkaar

## Nabewerkingstips

- Snijd afleidende elementen weg
- Recht muren en deuren in uw fotobewerkingsprogramma
- Maak afbeeldingen helderder en verscherp waar nodig
- Zorg voor consistente kwaliteit over alle foto's

## Begin met Fotograferen

Geweldige fotografie vereist geen dure apparatuur — alleen aandacht voor detail en de juiste techniek. Pas deze tips consequent toe en zie uw boekingsaanvragen toenemen.
    `,
  },

  es: {
    // Article 11: high-resolution-photos-portals
    'blog.article.photo-portals.title': 'Fotos en Alta Resolución y Etiquetas Ahora Enviadas a las OTAs',
    'blog.article.photo-portals.excerpt': 'Las fotos de tus propiedades ahora se publican en alta resolución con etiquetas descriptivas en Booking.com, Airbnb y otros portales principales.',
    'blog.article.photo-portals.content': `
## Mejores Fotos en Cada Plataforma

Hemos mejorado la forma en que i-Rent envía fotos a portales externos. Tus imágenes de alta resolución ahora se entregan en calidad completa, junto con etiquetas descriptivas que ayudan a las plataformas a categorizar y mostrar tus fotos de manera efectiva.

## Etiquetas de Fotos por Plataforma

Diferentes portales admiten el etiquetado de fotos de diferentes maneras:

- **Booking.com** — Admite múltiples etiquetas por foto
- **Airbnb** — Admite una etiqueta por foto
- **HomeAway** — Admite una etiqueta por foto con traducciones a múltiples idiomas

## Por Qué Importan las Etiquetas de Fotos

Las etiquetas de fotos ayudan a las plataformas de reservas a entender qué muestra cada imagen — ya sea un dormitorio, baño, piscina o vista. Esto mejora:

- **Visibilidad en búsquedas** — Las plataformas pueden hacer coincidir tus fotos con las búsquedas de los huéspedes
- **Experiencia del huésped** — Los viajeros encuentran lo que buscan más rápido
- **Calidad del anuncio** — Las fotos bien etiquetadas señalan un anuncio profesional

## Alta Resolución en Todas Partes

Junto con las etiquetas, tus fotos ahora se envían en alta resolución a los portales que lo admiten. Esto significa:

- **Imágenes más nítidas** — Sin más artefactos de compresión
- **Mejores primeras impresiones** — Las fotos de alta calidad atraen más clics
- **Presentación profesional** — Tus propiedades lucen lo mejor posible

## Comienza Ahora

Si aún no has probado nuestro sistema de fotos actualizado, ahora es el momento perfecto. Aprovecha las cargas en alta resolución y el etiquetado de fotos para que tus anuncios destaquen en cada plataforma.
    `,

    // Article 12: discounts-sync-airbnb
    'blog.article.airbnb-discounts.title': 'Los Descuentos Simples Ahora se Sincronizan con Airbnb',
    'blog.article.airbnb-discounts.excerpt': 'Tus descuentos basados en porcentaje creados en i-Rent ahora se envían automáticamente a Airbnb — ayudándote a atraer más reservas con promociones consistentes.',
    'blog.article.airbnb-discounts.content': `
## Descuentos en Airbnb, Gestionados desde i-Rent

Hemos ampliado nuestra integración con Airbnb para incluir la sincronización de descuentos. Los descuentos simples que creas en i-Rent ahora se envían automáticamente a tus anuncios de Airbnb.

## Requisitos para Descuentos de Airbnb

Para que un descuento se sincronice con Airbnb, debe cumplir estas condiciones:

- **Basado en porcentaje** — Solo se admiten descuentos que usan un porcentaje (%)
- **Sin estancia máxima** — El descuento no puede tener una restricción de estancia máxima

## Cómo se Aplican los Descuentos

Al enviar descuentos a Airbnb, i-Rent aplica las siguientes reglas:

### Configuración de Fechas
- **Fechas de inicio/fin de publicación** — Si se establecen, el descuento solo aparece durante ese período
- **Rango de fechas de llegada** — Si se especifica, el descuento se aplica a reservas que llegan dentro de esas fechas; si se deja vacío, el descuento se aplica a todos los días con precios

### Requisitos de Estancia
- **Estancia mínima** — Si estableces una estancia mínima, el descuento solo se aplica cuando es igual o menor que el requisito de estancia mínima del alojamiento
- **Estancia máxima** — No compatible con descuentos de Airbnb

### Método de Cálculo
- **Criterios de aplicación** — El descuento se aplica si al menos un día de la reserva cae dentro del período de descuento
- **Cálculo** — El descuento se calcula solo para los días que caen dentro del período de descuento

## Beneficios

- **Promociones consistentes** — Gestiona todos tus descuentos desde un solo lugar
- **Sincronización automática** — No necesitas actualizar Airbnb manualmente
- **Más reservas** — Los descuentos atractivos ayudan a llenar tu calendario
- **Control total** — Establece exactamente cuándo y cómo se aplican los descuentos

## Comienza a Crear Descuentos

Crea tus descuentos basados en porcentaje en i-Rent y observa cómo se sincronizan automáticamente con Airbnb. Es una forma sencilla de aumentar tu visibilidad y atraer más huéspedes.
    `,

    // Article 13: accommodation-categories-rental-types
    'blog.article.rental-categories.title': 'Categoriza Alojamientos por Tipo de Alquiler',
    'blog.article.rental-categories.excerpt': 'Organiza tu cartera distinguiendo entre alquileres vacacionales, alquileres a largo plazo y propiedades solo de mantenimiento — optimizando tu flujo de trabajo de publicación.',
    'blog.article.rental-categories.content': `
## Mejor Organización para Tus Propiedades

Hemos añadido una nueva función que te ayuda a organizar tus alojamientos de manera más efectiva categorizándolos según su propósito.

## Tres Categorías de Propiedades

Ahora puedes asignar cada alojamiento a una de tres categorías:

- **Alquileres vacacionales** — Propiedades disponibles para reservas de vacaciones a corto plazo
- **Alquileres a largo plazo** — Propiedades destinadas a estancias prolongadas o arrendamientos residenciales
- **Solo mantenimiento y servicio** — Propiedades no disponibles para reservas de huéspedes

## Cómo Configurar

Navega al menú **Información del Alojamiento** en i-Rent y selecciona la categoría apropiada para cada propiedad. La configuración es rápida y entra en vigor inmediatamente.

## Publicación Más Inteligente

El beneficio real viene al publicar en sitios web y portales:

- **Las propiedades solo de mantenimiento se excluyen automáticamente** de las listas de publicación
- **Sin más anuncios accidentales** de propiedades que no deberían estar disponibles para reservas
- **La publicación masiva se vuelve más segura** — selecciona grupos de alojamientos sin preocuparte por incluir las propiedades incorrectas

## Beneficios

- **Flujo de trabajo más limpio** — Enfócate solo en las propiedades que deben publicarse
- **Menos errores** — Evita que las propiedades de mantenimiento aparezcan en sitios de reservas
- **Mejor organización** — Separación clara entre diferentes tipos de alquiler
- **Ahorro de tiempo** — No necesitas excluir propiedades manualmente al publicar

## Comienza a Organizar

Toma unos minutos para categorizar tus alojamientos. Esta pequeña inversión de tiempo hará que tus operaciones diarias sean más fluidas y reducirá el riesgo de errores de publicación.
    `,

    // Article 14: holidu-online-payment-link
    'blog.article.holidu-payments.title': 'Enlaces de Pago en Línea Ahora Mostrados en Holidu',
    'blog.article.holidu-payments.excerpt': 'Las reservas de Holidu ahora incluyen enlaces de pago automáticos — los huéspedes ven la opción de pago directamente en la página de confirmación y en su correo electrónico.',
    'blog.article.holidu-payments.content': `
## Pagos Sin Problemas para Reservas de Holidu

Hemos integrado enlaces de pago automáticos para las reservas realizadas a través de Holidu. Esto agiliza el proceso de pago y mejora la experiencia del huésped.

## Cómo Funciona

Cuando Holidu crea una reserva, i-Rent envía automáticamente un enlace de pago en línea. Este enlace luego:

- **Se muestra en la página de confirmación de reserva de Holidu** — los huéspedes pueden pagar inmediatamente después de reservar
- **Se incluye en el correo electrónico de confirmación** — fácil acceso para huéspedes que prefieren pagar más tarde

El enlace de pago incluye el monto del alquiler más cualquier extra obligatorio que deba pagarse en el momento de la reserva.

## Requisitos

Esta función está disponible para administradores de propiedades que tienen un **sistema POS (Punto de Venta)** configurado en i-Rent. Si aún no has configurado tu sistema de pago, contacta a nuestro equipo de soporte para asistencia.

## Beneficios

- **Pagos más rápidos** — Los huéspedes pueden pagar inmediatamente sin esperar facturas manuales
- **Mejor conversión** — Reducir la fricción lleva a más reservas completadas
- **Experiencia profesional** — Las páginas de pago con marca generan confianza con los huéspedes
- **Seguimiento automático** — Todos los pagos se sincronizan directamente con tu panel de i-Rent

## Comienza Ahora

Si ya estás conectado a Holidu con un sistema POS en i-Rent, esta función está activa automáticamente. Comienza a disfrutar de flujos de pago más fluidos para tus reservas de Holidu hoy mismo.
    `,

    // Article 15: better-photos-holiday-homes
    'blog.article.photo-tips.title': 'Cómo Tomar Mejores Fotos de Casas de Vacaciones',
    'blog.article.photo-tips.excerpt': 'Consejos de fotografía profesional para alquileres vacacionales — desde tomas exteriores hasta staging interior, aprende a capturar imágenes que conviertan visitantes en reservas.',
    'blog.article.photo-tips.content': `
## Por Qué Importan las Grandes Fotos

La fotografía de calidad es uno de los factores más importantes para atraer reservas. Estos consejos profesionales te ayudarán a capturar imágenes que muestren tus propiedades de la mejor manera.

## Fundamentos de la Cámara

Antes de empezar a fotografiar, ten en cuenta estos fundamentos:

- **Siempre fotografía en formato horizontal** para evitar problemas de recorte en OTAs y portales
- **Usa la máxima resolución** para las imágenes más nítidas posibles
- **Mantén la cámara nivelada** (aproximadamente 1,5m del suelo) para evitar paredes y puertas inclinadas
- **Usa un objetivo gran angular** para capturar más de cada espacio
- **Toma fotos de día más 1-2 fotos nocturnas** justo después del atardecer cuando el cielo todavía está azul

## Fotografía Exterior

### Directrices Generales

1. **Solo fotografía en días soleados** — pequeñas nubes blancas son aceptables
2. **Posiciona el sol detrás de ti** para cielos azules e iluminación uniforme
3. **Abre todas las persianas, puertas y cortinas** visibles desde el exterior
4. **Minimiza los edificios vecinos** en tu encuadre — evita mostrar ventanas de casas circundantes

### Jardín y Piscina

- Prepara el área exactamente como los huéspedes la encontrarían al llegar
- Organiza las tumbonas y muebles de manera ordenada e invitadora
- Añade toallas en las tumbonas y usa decoración de buen gusto
- Oculta mangueras de jardín y objetos desordenados
- **Consejo profesional:** Revuelve el agua de la piscina con una escoba antes de fotografiar para ocultar cualquier residuo en el fondo

### Terrazas

- Mantén todo limpio y ordenado
- Pon la mesa completamente con platos, cubiertos, vino y fruta
- Crea una atmósfera que invite a cenar

### Vistas

- Minimiza los edificios circundantes en el encuadre
- Fotografía con el sol detrás de ti
- Muestra que la vista pertenece a esta propiedad específica

## Fotografía Interior

### Consejos Generales de Interior

- **Enciende TODAS las luces** — cada una de ellas
- **Ordena a fondo** y elimina el desorden
- **Fotografía desde las esquinas** con no más de 2 paredes visibles — esto crea mejor sensación espacial
- **Reorganiza los muebles** para crear más espacio; evita fotografiar la parte trasera de los sofás
- **Oculta cables** y cables eléctricos a lo largo de las paredes
- **Alisa los cojines** o retíralos para composiciones más limpias
- Usa decoración, pero no te excedas

### Gestión de la Luz

- Si entra demasiada luz solar por las ventanas, cierra las cortinas o cubre las ventanas desde fuera
- Para tomas a través de ventanas hacia un exterior brillante, usa un trípode y haz dos exposiciones — una para la vista, otra para el interior — luego combínalas en Photoshop

### Salones y Comedores

- Retira alfombras, fundas de sofá y cojines excesivos
- Retira objetos personales como fotos familiares
- Abre ventanas y puertas cuando la luz lo permita
- Captura tomas de detalles interesantes

### Dormitorios

- Usa solo sábanas o edredones blancos
- Añade toallas enrolladas en la cama y múltiples almohadas
- Retira todos los objetos personales
- En habitaciones pequeñas, enfócate en el detalle de la cama con mesitas de noche

### Baños

- Retira todos los objetos personales
- Si el espacio es demasiado estrecho para orientación horizontal, toma dos fotos en vertical y combínalas una al lado de la otra

## Consejos de Postproducción

- Recorta cualquier elemento que distraiga
- Endereza paredes y puertas en tu editor de fotos
- Aclara las imágenes y enfoca donde sea necesario
- Asegura una calidad consistente en todas las fotos

## Comienza a Fotografiar

La gran fotografía no requiere equipo costoso — solo atención al detalle y técnica adecuada. Aplica estos consejos consistentemente y observa cómo aumentan tus consultas de reserva.
    `,
  },

  de: {
    // Article 11: high-resolution-photos-portals
    'blog.article.photo-portals.title': 'Hochauflösende Fotos und Tags Werden Jetzt an OTAs Gesendet',
    'blog.article.photo-portals.excerpt': 'Ihre Immobilienfotos werden jetzt in hoher Auflösung mit beschreibenden Tags an Booking.com, Airbnb und andere große Portale veröffentlicht.',
    'blog.article.photo-portals.content': `
## Bessere Fotos auf Jeder Plattform

Wir haben verbessert, wie i-Rent Fotos an externe Portale sendet. Ihre hochauflösenden Bilder werden jetzt in voller Qualität geliefert, zusammen mit beschreibenden Tags, die Plattformen helfen, Ihre Fotos effektiv zu kategorisieren und anzuzeigen.

## Foto-Tags nach Plattform

Verschiedene Portale unterstützen Foto-Tagging auf unterschiedliche Weise:

- **Booking.com** — Unterstützt mehrere Tags pro Foto
- **Airbnb** — Unterstützt ein Tag pro Foto
- **HomeAway** — Unterstützt ein Tag pro Foto mit mehreren Sprachübersetzungen

## Warum Foto-Tags Wichtig Sind

Foto-Tags helfen Buchungsplattformen zu verstehen, was jedes Bild zeigt — ob es ein Schlafzimmer, Badezimmer, Pool oder Aussicht ist. Dies verbessert:

- **Suchsichtbarkeit** — Plattformen können Ihre Fotos mit Gästeanfragen abgleichen
- **Gästeerlebnis** — Reisende finden schneller, was sie suchen
- **Inseratsqualität** — Gut getaggte Fotos signalisieren ein professionelles Inserat

## Hohe Auflösung Überall

Zusammen mit Tags werden Ihre Fotos jetzt in hoher Auflösung an Portale gesendet, die dies unterstützen. Das bedeutet:

- **Schärfere Bilder** — Keine Kompressionsartefakte mehr
- **Bessere erste Eindrücke** — Hochwertige Fotos ziehen mehr Klicks an
- **Professionelle Präsentation** — Ihre Unterkünfte sehen bestens aus

## Jetzt Starten

Wenn Sie unser aktualisiertes Fotosystem noch nicht ausprobiert haben, ist jetzt der perfekte Zeitpunkt. Nutzen Sie hochauflösende Uploads und Foto-Tagging, um Ihre Inserate auf jeder Plattform hervorzuheben.
    `,

    // Article 12: discounts-sync-airbnb
    'blog.article.airbnb-discounts.title': 'Einfache Rabatte Werden Jetzt mit Airbnb Synchronisiert',
    'blog.article.airbnb-discounts.excerpt': 'Ihre prozentbasierten Rabatte, die in i-Rent erstellt wurden, werden jetzt automatisch an Airbnb gesendet — und helfen Ihnen, mehr Buchungen mit konsistenten Aktionen anzuziehen.',
    'blog.article.airbnb-discounts.content': `
## Rabatte auf Airbnb, Verwaltet von i-Rent

Wir haben unsere Airbnb-Integration um die Rabattsynchronisation erweitert. Einfache Rabatte, die Sie in i-Rent erstellen, werden jetzt automatisch an Ihre Airbnb-Inserate gesendet.

## Voraussetzungen für Airbnb-Rabatte

Damit ein Rabatt mit Airbnb synchronisiert wird, muss er diese Bedingungen erfüllen:

- **Prozentbasiert** — Nur Rabatte mit einem Prozentsatz (%) werden unterstützt
- **Kein maximaler Aufenthalt** — Der Rabatt darf keine Beschränkung für maximalen Aufenthalt haben

## Wie Rabatte Angewendet Werden

Beim Senden von Rabatten an Airbnb wendet i-Rent folgende Regeln an:

### Datumseinstellungen
- **Start-/Enddatum der Veröffentlichung** — Falls gesetzt, erscheint der Rabatt nur während dieses Zeitraums
- **Anreisedatumsbereich** — Falls angegeben, gilt der Rabatt für Buchungen, die innerhalb dieser Daten anreisen; wenn leer gelassen, gilt der Rabatt für alle Tage mit Preisen

### Aufenthaltsanforderungen
- **Mindestaufenthalt** — Wenn Sie einen Mindestaufenthalt festlegen, gilt der Rabatt nur, wenn dieser gleich oder kleiner als die Mindestaufenthaltsanforderung der Unterkunft ist
- **Maximaler Aufenthalt** — Nicht unterstützt für Airbnb-Rabatte

### Berechnungsmethode
- **Anwendungskriterien** — Der Rabatt gilt, wenn mindestens ein Tag der Buchung in den Rabattzeitraum fällt
- **Berechnung** — Der Rabatt wird nur für Tage berechnet, die in den Rabattzeitraum fallen

## Vorteile

- **Konsistente Aktionen** — Verwalten Sie alle Ihre Rabatte von einem Ort aus
- **Automatische Synchronisation** — Kein manuelles Aktualisieren von Airbnb erforderlich
- **Mehr Buchungen** — Attraktive Rabatte helfen, Ihren Kalender zu füllen
- **Volle Kontrolle** — Legen Sie genau fest, wann und wie Rabatte gelten

## Beginnen Sie mit dem Erstellen von Rabatten

Erstellen Sie Ihre prozentbasierten Rabatte in i-Rent und beobachten Sie, wie sie automatisch mit Airbnb synchronisiert werden. Es ist eine einfache Möglichkeit, Ihre Sichtbarkeit zu erhöhen und mehr Gäste anzuziehen.
    `,

    // Article 13: accommodation-categories-rental-types
    'blog.article.rental-categories.title': 'Kategorisieren Sie Unterkünfte nach Vermietungstyp',
    'blog.article.rental-categories.excerpt': 'Organisieren Sie Ihr Portfolio durch Unterscheidung zwischen Ferienvermietungen, Langzeitvermietungen und reinen Wartungsimmobilien — optimieren Sie Ihren Veröffentlichungs-Workflow.',
    'blog.article.rental-categories.content': `
## Bessere Organisation für Ihre Immobilien

Wir haben eine neue Funktion hinzugefügt, die Ihnen hilft, Ihre Unterkünfte effektiver zu organisieren, indem Sie sie nach ihrem Zweck kategorisieren.

## Drei Immobilienkategorien

Sie können jetzt jede Unterkunft einer von drei Kategorien zuweisen:

- **Ferienvermietungen** — Immobilien, die für kurzfristige Urlaubsbuchungen verfügbar sind
- **Langzeitvermietungen** — Immobilien für längere Aufenthalte oder Wohnmieten vorgesehen
- **Nur Wartung und Service** — Immobilien, die nicht für Gästebuchungen verfügbar sind

## Einrichtung

Navigieren Sie zum Menü **Unterkunftsinformationen** in i-Rent und wählen Sie die entsprechende Kategorie für jede Immobilie. Die Einstellung ist schnell zu konfigurieren und tritt sofort in Kraft.

## Intelligenteres Veröffentlichen

Der echte Vorteil zeigt sich beim Veröffentlichen auf Websites und Portalen:

- **Reine Wartungsimmobilien werden automatisch ausgeschlossen** von Veröffentlichungslisten
- **Keine versehentlichen Inserate mehr** von Immobilien, die nicht buchbar sein sollten
- **Massenveröffentlichung wird sicherer** — wählen Sie Gruppen von Unterkünften, ohne sich Sorgen machen zu müssen, die falschen Immobilien einzuschließen

## Vorteile

- **Sauberer Workflow** — Konzentrieren Sie sich nur auf Immobilien, die veröffentlicht werden sollen
- **Weniger Fehler** — Verhindern Sie, dass Wartungsimmobilien auf Buchungsseiten erscheinen
- **Bessere Organisation** — Klare Trennung zwischen verschiedenen Vermietungstypen
- **Zeitersparnis** — Kein manuelles Ausschließen von Immobilien beim Veröffentlichen erforderlich

## Beginnen Sie mit dem Organisieren

Nehmen Sie sich ein paar Minuten Zeit, um Ihre Unterkünfte zu kategorisieren. Diese kleine Zeitinvestition macht Ihre täglichen Abläufe reibungsloser und reduziert das Risiko von Veröffentlichungsfehlern.
    `,

    // Article 14: holidu-online-payment-link
    'blog.article.holidu-payments.title': 'Online-Zahlungslinks Werden Jetzt auf Holidu Angezeigt',
    'blog.article.holidu-payments.excerpt': 'Holidu-Buchungen enthalten jetzt automatische Zahlungslinks — Gäste sehen die Zahlungsoption direkt auf der Bestätigungsseite und in ihrer E-Mail.',
    'blog.article.holidu-payments.content': `
## Nahtlose Zahlungen für Holidu-Buchungen

Wir haben automatische Zahlungslinks für Buchungen über Holidu integriert. Dies optimiert den Zahlungsprozess und verbessert das Gästeerlebnis.

## Wie Es Funktioniert

Wenn Holidu eine Buchung erstellt, sendet i-Rent automatisch einen Online-Zahlungslink zurück. Dieser Link wird dann:

- **Auf der Buchungsbestätigungsseite von Holidu angezeigt** — Gäste können sofort nach der Buchung bezahlen
- **In der Bestätigungs-E-Mail enthalten** — einfacher Zugang für Gäste, die lieber später bezahlen

Der Zahlungslink enthält den Mietbetrag plus alle obligatorischen Extras, die zum Zeitpunkt der Buchung bezahlt werden müssen.

## Voraussetzungen

Diese Funktion ist für Immobilienverwalter verfügbar, die ein **POS (Point of Sale) System** in i-Rent konfiguriert haben. Wenn Sie Ihr Zahlungssystem noch nicht eingerichtet haben, kontaktieren Sie unser Support-Team für Unterstützung.

## Vorteile

- **Schnellere Zahlungen** — Gäste können sofort bezahlen, ohne auf manuelle Rechnungen zu warten
- **Bessere Konversion** — Weniger Reibung führt zu mehr abgeschlossenen Buchungen
- **Professionelles Erlebnis** — Gebrandete Zahlungsseiten bauen Vertrauen bei Gästen auf
- **Automatisches Tracking** — Alle Zahlungen werden direkt mit Ihrem i-Rent-Dashboard synchronisiert

## Jetzt Starten

Wenn Sie bereits mit einem POS-System in i-Rent mit Holidu verbunden sind, ist diese Funktion automatisch aktiv. Genießen Sie ab heute reibungslosere Zahlungsabläufe für Ihre Holidu-Buchungen.
    `,

    // Article 15: better-photos-holiday-homes
    'blog.article.photo-tips.title': 'Wie Sie Bessere Fotos von Ferienhäusern Machen',
    'blog.article.photo-tips.excerpt': 'Professionelle Fotografietipps für Ferienvermietungen — von Außenaufnahmen bis zur Innenraumgestaltung, lernen Sie, wie Sie Bilder aufnehmen, die Besucher in Bucher verwandeln.',
    'blog.article.photo-tips.content': `
## Warum Großartige Fotos Wichtig Sind

Qualitätsfotografie ist einer der wichtigsten Faktoren bei der Gewinnung von Buchungen. Diese professionellen Tipps helfen Ihnen, Bilder aufzunehmen, die Ihre Immobilien von ihrer besten Seite zeigen.

## Kamera-Grundlagen

Bevor Sie mit dem Fotografieren beginnen, beachten Sie diese Grundlagen:

- **Fotografieren Sie immer im Querformat** um Beschnittprobleme auf OTAs und Portalen zu vermeiden
- **Verwenden Sie maximale Auflösung** für die schärfsten Bilder
- **Halten Sie die Kamera waagerecht** (etwa 1,5m vom Boden) um schiefe Wände und Türen zu vermeiden
- **Verwenden Sie ein Weitwinkelobjektiv** um mehr von jedem Raum aufzunehmen
- **Machen Sie Tagesaufnahmen plus 1-2 Abendaufnahmen** kurz nach Sonnenuntergang, wenn der Himmel noch blau ist

## Außenfotografie

### Allgemeine Richtlinien

1. **Fotografieren Sie nur an sonnigen Tagen** — kleine weiße Wolken sind akzeptabel
2. **Positionieren Sie die Sonne hinter sich** für blauen Himmel und gleichmäßige Beleuchtung
3. **Öffnen Sie alle Jalousien, Türen und Vorhänge** die von außen sichtbar sind
4. **Minimieren Sie Nachbargebäude** in Ihrem Bild — vermeiden Sie es, Fenster von umliegenden Häusern zu zeigen

### Garten & Pool

- Bereiten Sie den Bereich genau so vor, wie Gäste ihn bei der Ankunft vorfinden würden
- Ordnen Sie Sonnenliegen und Möbel ordentlich und einladend an
- Legen Sie Handtücher auf Liegen und verwenden Sie geschmackvolle Dekoration
- Verstecken Sie Gartenschläuche und unordentliche Gegenstände
- **Profi-Tipp:** Rühren Sie das Poolwasser mit einem Besen vor dem Fotografieren um, um Schmutz am Boden zu verbergen

### Terrassen

- Halten Sie alles sauber und aufgeräumt
- Decken Sie den Tisch komplett mit Tellern, Besteck, Wein und Obst
- Schaffen Sie eine Atmosphäre, die zum Essen einlädt

### Aussichten

- Minimieren Sie umliegende Gebäude im Bild
- Fotografieren Sie mit der Sonne hinter sich
- Zeigen Sie, dass die Aussicht zu dieser spezifischen Immobilie gehört

## Innenfotografie

### Allgemeine Innentipps

- **Schalten Sie ALLE Lichter ein** — jedes einzelne
- **Räumen Sie gründlich auf** und entfernen Sie Unordnung
- **Fotografieren Sie aus Ecken** mit nicht mehr als 2 sichtbaren Wänden — dies erzeugt ein besseres Raumgefühl
- **Ordnen Sie Möbel um** um mehr Platz zu schaffen; vermeiden Sie es, Rückseiten von Sofas zu fotografieren
- **Verstecken Sie Kabel** und elektrische Leitungen entlang der Wände
- **Glätten Sie Kissen** oder entfernen Sie sie für sauberere Kompositionen
- Verwenden Sie Dekoration, aber übertreiben Sie es nicht

### Lichtmanagement

- Wenn zu viel Sonnenlicht durch Fenster einfällt, schließen Sie Vorhänge oder decken Sie Fenster von außen ab
- Für Aufnahmen durch Fenster zu einem hellen Außenbereich verwenden Sie ein Stativ und machen Sie zwei Belichtungen — eine für die Aussicht, eine für den Innenraum — kombinieren Sie sie dann in Photoshop

### Wohn- & Esszimmer

- Entfernen Sie Teppiche, Sofabezüge und überschüssige Kissen
- Entfernen Sie persönliche Gegenstände wie Familienfotos
- Öffnen Sie Fenster und Türen, wenn das Licht es zulässt
- Nehmen Sie interessante Detailaufnahmen auf

### Schlafzimmer

- Verwenden Sie nur weiße Laken oder Bettdecken
- Legen Sie gerollte Handtücher auf das Bett und mehrere Kissen
- Entfernen Sie alle persönlichen Gegenstände
- In kleinen Räumen konzentrieren Sie sich auf das Bettdetail mit Nachttischen

### Badezimmer

- Entfernen Sie alle persönlichen Gegenstände
- Wenn der Platz zu eng für Querformat ist, machen Sie zwei Hochformataufnahmen und kombinieren Sie sie nebeneinander

## Nachbearbeitungstipps

- Schneiden Sie ablenkende Elemente weg
- Begradigen Sie Wände und Türen in Ihrem Bildbearbeitungsprogramm
- Hellen Sie Bilder auf und schärfen Sie nach Bedarf
- Stellen Sie eine konsistente Qualität über alle Fotos sicher

## Beginnen Sie mit dem Fotografieren

Großartige Fotografie erfordert keine teure Ausrüstung — nur Aufmerksamkeit für Details und die richtige Technik. Wenden Sie diese Tipps konsequent an und beobachten Sie, wie Ihre Buchungsanfragen steigen.
    `,
  },

  fr: {
    // Article 11: high-resolution-photos-portals
    'blog.article.photo-portals.title': 'Photos Haute Résolution et Tags Maintenant Envoyés aux OTAs',
    'blog.article.photo-portals.excerpt': 'Vos photos de propriétés sont maintenant publiées en haute résolution avec des tags descriptifs sur Booking.com, Airbnb et autres grands portails.',
    'blog.article.photo-portals.content': `
## De Meilleures Photos sur Chaque Plateforme

Nous avons amélioré la façon dont i-Rent envoie les photos aux portails externes. Vos images haute résolution sont maintenant livrées en qualité complète, accompagnées de tags descriptifs qui aident les plateformes à catégoriser et afficher vos photos efficacement.

## Tags Photo par Plateforme

Différents portails prennent en charge le tagging photo de différentes manières :

- **Booking.com** — Prend en charge plusieurs tags par photo
- **Airbnb** — Prend en charge un tag par photo
- **HomeAway** — Prend en charge un tag par photo avec des traductions multilingues

## Pourquoi les Tags Photo Sont Importants

Les tags photo aident les plateformes de réservation à comprendre ce que montre chaque image — qu'il s'agisse d'une chambre, d'une salle de bain, d'une piscine ou d'une vue. Cela améliore :

- **Visibilité de recherche** — Les plateformes peuvent faire correspondre vos photos aux recherches des clients
- **Expérience client** — Les voyageurs trouvent plus rapidement ce qu'ils cherchent
- **Qualité de l'annonce** — Des photos bien tagguées signalent une annonce professionnelle

## Haute Résolution Partout

Avec les tags, vos photos sont maintenant envoyées en haute résolution aux portails qui le supportent. Cela signifie :

- **Images plus nettes** — Plus d'artefacts de compression
- **Meilleures premières impressions** — Les photos de haute qualité attirent plus de clics
- **Présentation professionnelle** — Vos propriétés sont présentées sous leur meilleur jour

## Commencez Maintenant

Si vous n'avez pas encore essayé notre système photo mis à jour, c'est le moment idéal. Profitez des téléchargements haute résolution et du tagging photo pour faire ressortir vos annonces sur chaque plateforme.
    `,

    // Article 12: discounts-sync-airbnb
    'blog.article.airbnb-discounts.title': 'Les Remises Simples Se Synchronisent Maintenant avec Airbnb',
    'blog.article.airbnb-discounts.excerpt': 'Vos remises en pourcentage créées dans i-Rent sont maintenant automatiquement envoyées à Airbnb — vous aidant à attirer plus de réservations avec des promotions cohérentes.',
    'blog.article.airbnb-discounts.content': `
## Remises sur Airbnb, Gérées depuis i-Rent

Nous avons étendu notre intégration Airbnb pour inclure la synchronisation des remises. Les remises simples que vous créez dans i-Rent sont maintenant automatiquement envoyées à vos annonces Airbnb.

## Exigences pour les Remises Airbnb

Pour qu'une remise soit synchronisée avec Airbnb, elle doit remplir ces conditions :

- **Basée sur un pourcentage** — Seules les remises utilisant un pourcentage (%) sont prises en charge
- **Pas de séjour maximum** — La remise ne peut pas avoir de restriction de séjour maximum

## Comment les Remises Sont Appliquées

Lors de l'envoi des remises à Airbnb, i-Rent applique les règles suivantes :

### Paramètres de Date
- **Dates de début/fin de publication** — Si définies, la remise n'apparaît que pendant cette période
- **Plage de dates d'arrivée** — Si spécifiée, la remise s'applique aux réservations arrivant dans ces dates ; si laissé vide, la remise s'applique à tous les jours avec tarification

### Exigences de Séjour
- **Séjour minimum** — Si vous définissez un séjour minimum, la remise ne s'applique que lorsqu'il est égal ou inférieur à l'exigence de séjour minimum de l'hébergement
- **Séjour maximum** — Non pris en charge pour les remises Airbnb

### Méthode de Calcul
- **Critères d'application** — La remise s'applique si au moins un jour de la réservation tombe dans la période de remise
- **Calcul** — La remise n'est calculée que pour les jours qui tombent dans la période de remise

## Avantages

- **Promotions cohérentes** — Gérez toutes vos remises depuis un seul endroit
- **Synchronisation automatique** — Pas besoin de mettre à jour Airbnb manuellement
- **Plus de réservations** — Les remises attractives aident à remplir votre calendrier
- **Contrôle total** — Définissez exactement quand et comment les remises s'appliquent

## Commencez à Créer des Remises

Créez vos remises en pourcentage dans i-Rent et regardez-les se synchroniser automatiquement avec Airbnb. C'est un moyen simple d'augmenter votre visibilité et d'attirer plus de clients.
    `,

    // Article 13: accommodation-categories-rental-types
    'blog.article.rental-categories.title': 'Catégorisez les Hébergements par Type de Location',
    'blog.article.rental-categories.excerpt': 'Organisez votre portefeuille en distinguant les locations de vacances, les locations longue durée et les propriétés de maintenance uniquement — rationalisez votre flux de publication.',
    'blog.article.rental-categories.content': `
## Une Meilleure Organisation pour Vos Propriétés

Nous avons ajouté une nouvelle fonctionnalité qui vous aide à organiser vos hébergements plus efficacement en les catégorisant selon leur objectif.

## Trois Catégories de Propriétés

Vous pouvez maintenant attribuer chaque hébergement à l'une des trois catégories :

- **Locations de vacances** — Propriétés disponibles pour les réservations de vacances à court terme
- **Locations longue durée** — Propriétés destinées aux séjours prolongés ou aux baux résidentiels
- **Maintenance et service uniquement** — Propriétés non disponibles pour les réservations clients

## Comment Configurer

Naviguez vers le menu **Informations sur l'Hébergement** dans i-Rent et sélectionnez la catégorie appropriée pour chaque propriété. Le paramètre est rapide à configurer et prend effet immédiatement.

## Publication Plus Intelligente

Le vrai avantage vient lors de la publication sur les sites Web et portails :

- **Les propriétés de maintenance sont automatiquement exclues** des listes de publication
- **Plus d'annonces accidentelles** de propriétés qui ne devraient pas être réservables
- **La publication en masse devient plus sûre** — sélectionnez des groupes d'hébergements sans vous soucier d'inclure les mauvaises propriétés

## Avantages

- **Flux de travail plus propre** — Concentrez-vous uniquement sur les propriétés à publier
- **Moins d'erreurs** — Empêchez les propriétés de maintenance d'apparaître sur les sites de réservation
- **Meilleure organisation** — Séparation claire entre les différents types de location
- **Gain de temps** — Pas besoin d'exclure manuellement les propriétés lors de la publication

## Commencez à Organiser

Prenez quelques minutes pour catégoriser vos hébergements. Ce petit investissement de temps rendra vos opérations quotidiennes plus fluides et réduira le risque d'erreurs de publication.
    `,

    // Article 14: holidu-online-payment-link
    'blog.article.holidu-payments.title': 'Liens de Paiement en Ligne Maintenant Affichés sur Holidu',
    'blog.article.holidu-payments.excerpt': 'Les réservations Holidu incluent maintenant des liens de paiement automatiques — les clients voient l\'option de paiement directement sur la page de confirmation et dans leur email.',
    'blog.article.holidu-payments.content': `
## Paiements Fluides pour les Réservations Holidu

Nous avons intégré des liens de paiement automatiques pour les réservations effectuées via Holidu. Cela rationalise le processus de paiement et améliore l'expérience client.

## Comment Ça Fonctionne

Lorsque Holidu crée une réservation, i-Rent renvoie automatiquement un lien de paiement en ligne. Ce lien est ensuite :

- **Affiché sur la page de confirmation de réservation Holidu** — les clients peuvent payer immédiatement après la réservation
- **Inclus dans l'email de confirmation** — accès facile pour les clients qui préfèrent payer plus tard

Le lien de paiement inclut le montant de la location plus tous les extras obligatoires à payer au moment de la réservation.

## Exigences

Cette fonctionnalité est disponible pour les gestionnaires de propriétés qui ont un **système POS (Point de Vente)** configuré dans i-Rent. Si vous n'avez pas encore configuré votre système de paiement, contactez notre équipe support pour assistance.

## Avantages

- **Paiements plus rapides** — Les clients peuvent payer immédiatement sans attendre les factures manuelles
- **Meilleure conversion** — Réduire les frictions mène à plus de réservations complétées
- **Expérience professionnelle** — Les pages de paiement brandées renforcent la confiance des clients
- **Suivi automatique** — Tous les paiements se synchronisent directement avec votre tableau de bord i-Rent

## Commencez Maintenant

Si vous êtes déjà connecté à Holidu avec un système POS dans i-Rent, cette fonctionnalité est active automatiquement. Commencez à profiter de flux de paiement plus fluides pour vos réservations Holidu dès aujourd'hui.
    `,

    // Article 15: better-photos-holiday-homes
    'blog.article.photo-tips.title': 'Comment Prendre de Meilleures Photos de Maisons de Vacances',
    'blog.article.photo-tips.excerpt': 'Conseils de photographie professionnelle pour les locations de vacances — des prises de vue extérieures à la mise en scène intérieure, apprenez à capturer des images qui convertissent les visiteurs en réservations.',
    'blog.article.photo-tips.content': `
## Pourquoi les Bonnes Photos Comptent

La photographie de qualité est l'un des facteurs les plus importants pour attirer les réservations. Ces conseils professionnels vous aideront à capturer des images qui mettent en valeur vos propriétés.

## Les Bases de l'Appareil Photo

Avant de commencer à photographier, gardez ces fondamentaux en tête :

- **Photographiez toujours en format paysage** pour éviter les problèmes de recadrage sur les OTAs et portails
- **Utilisez la résolution maximale** pour les images les plus nettes possibles
- **Maintenez l'appareil photo de niveau** (environ 1,5m du sol) pour éviter les murs et portes déformés
- **Utilisez un objectif grand-angle** pour capturer plus de chaque espace
- **Prenez des photos de jour plus 1-2 photos du soir** juste après le coucher du soleil quand le ciel est encore bleu

## Photographie Extérieure

### Lignes Directrices Générales

1. **Photographiez uniquement par temps ensoleillé** — de petits nuages blancs sont acceptables
2. **Positionnez le soleil derrière vous** pour des ciels bleus et un éclairage uniforme
3. **Ouvrez tous les stores, portes et rideaux** visibles de l'extérieur
4. **Minimisez les bâtiments voisins** dans votre cadre — évitez de montrer les fenêtres des maisons environnantes

### Jardin & Piscine

- Préparez la zone exactement comme les clients la trouveraient à leur arrivée
- Disposez les transats et mobilier de manière ordonnée et accueillante
- Ajoutez des serviettes sur les transats et utilisez une décoration de bon goût
- Cachez les tuyaux d'arrosage et objets désordonnés
- **Astuce pro :** Remuez l'eau de la piscine avec un balai avant de photographier pour cacher les débris au fond

### Terrasses

- Gardez tout propre et rangé
- Dressez la table complètement avec assiettes, couverts, vin et fruits
- Créez une atmosphère qui invite à dîner

### Vues

- Minimisez les bâtiments environnants dans le cadre
- Photographiez avec le soleil derrière vous
- Montrez que la vue appartient à cette propriété spécifique

## Photographie Intérieure

### Conseils Généraux d'Intérieur

- **Allumez TOUTES les lumières** — chacune d'entre elles
- **Rangez soigneusement** et éliminez le désordre
- **Photographiez depuis les coins** avec pas plus de 2 murs visibles — cela crée une meilleure sensation d'espace
- **Réarrangez les meubles** pour créer plus d'espace ; évitez de photographier le dos des canapés
- **Cachez les câbles** et fils électriques le long des murs
- **Lissez les coussins** ou retirez-les pour des compositions plus propres
- Utilisez la décoration, mais n'en faites pas trop

### Gestion de la Lumière

- Si trop de lumière solaire entre par les fenêtres, fermez les rideaux ou couvrez les fenêtres de l'extérieur
- Pour les prises de vue à travers les fenêtres vers un extérieur lumineux, utilisez un trépied et faites deux expositions — une pour la vue, une pour l'intérieur — puis combinez dans Photoshop

### Salons & Salles à Manger

- Retirez tapis, housses de canapé et coussins excédentaires
- Retirez les objets personnels comme les photos de famille
- Ouvrez fenêtres et portes quand la lumière le permet
- Capturez des photos de détails intéressantes

### Chambres

- Utilisez uniquement des draps ou couettes blancs
- Ajoutez des serviettes roulées sur le lit et plusieurs oreillers
- Retirez tous les objets personnels
- Dans les petites pièces, concentrez-vous sur le détail du lit avec les tables de chevet

### Salles de Bain

- Retirez tous les objets personnels
- Si l'espace est trop étroit pour l'orientation paysage, prenez deux photos en portrait et combinez-les côte à côte

## Conseils de Post-Production

- Recadrez tout élément distrayant
- Redressez les murs et portes dans votre éditeur photo
- Éclaircissez les images et affûtez si nécessaire
- Assurez une qualité constante sur toutes les photos

## Commencez à Photographier

La bonne photographie ne nécessite pas d'équipement coûteux — juste l'attention aux détails et la bonne technique. Appliquez ces conseils régulièrement et regardez vos demandes de réservation augmenter.
    `,
  },

  it: {
    // Article 11: high-resolution-photos-portals
    'blog.article.photo-portals.title': 'Foto ad Alta Risoluzione e Tag Ora Inviati alle OTA',
    'blog.article.photo-portals.excerpt': 'Le foto delle tue proprietà sono ora pubblicate in alta risoluzione con tag descrittivi su Booking.com, Airbnb e altri portali principali.',
    'blog.article.photo-portals.content': `
## Foto Migliori su Ogni Piattaforma

Abbiamo migliorato il modo in cui i-Rent invia le foto ai portali esterni. Le tue immagini ad alta risoluzione vengono ora consegnate in qualità completa, insieme a tag descrittivi che aiutano le piattaforme a categorizzare e visualizzare le tue foto efficacemente.

## Tag Foto per Piattaforma

Diversi portali supportano il tagging delle foto in modi diversi:

- **Booking.com** — Supporta più tag per foto
- **Airbnb** — Supporta un tag per foto
- **HomeAway** — Supporta un tag per foto con traduzioni in più lingue

## Perché i Tag Foto Sono Importanti

I tag foto aiutano le piattaforme di prenotazione a capire cosa mostra ogni immagine — che sia una camera da letto, bagno, piscina o vista. Questo migliora:

- **Visibilità nella ricerca** — Le piattaforme possono abbinare le tue foto alle ricerche degli ospiti
- **Esperienza ospite** — I viaggiatori trovano più velocemente ciò che cercano
- **Qualità dell'annuncio** — Foto ben taggate segnalano un annuncio professionale

## Alta Risoluzione Ovunque

Insieme ai tag, le tue foto vengono ora inviate in alta risoluzione ai portali che lo supportano. Questo significa:

- **Immagini più nitide** — Niente più artefatti di compressione
- **Migliori prime impressioni** — Le foto di alta qualità attirano più clic
- **Presentazione professionale** — Le tue proprietà appaiono al meglio

## Inizia Ora

Se non hai ancora provato il nostro sistema foto aggiornato, ora è il momento perfetto. Approfitta dei caricamenti ad alta risoluzione e del tagging foto per far risaltare i tuoi annunci su ogni piattaforma.
    `,

    // Article 12: discounts-sync-airbnb
    'blog.article.airbnb-discounts.title': 'Gli Sconti Semplici Ora Si Sincronizzano con Airbnb',
    'blog.article.airbnb-discounts.excerpt': 'I tuoi sconti percentuali creati in i-Rent vengono ora automaticamente inviati ad Airbnb — aiutandoti ad attirare più prenotazioni con promozioni coerenti.',
    'blog.article.airbnb-discounts.content': `
## Sconti su Airbnb, Gestiti da i-Rent

Abbiamo ampliato la nostra integrazione Airbnb per includere la sincronizzazione degli sconti. Gli sconti semplici che crei in i-Rent vengono ora automaticamente inviati ai tuoi annunci Airbnb.

## Requisiti per gli Sconti Airbnb

Affinché uno sconto si sincronizzi con Airbnb, deve soddisfare queste condizioni:

- **Basato su percentuale** — Solo gli sconti che usano una percentuale (%) sono supportati
- **Nessun soggiorno massimo** — Lo sconto non può avere una restrizione di soggiorno massimo

## Come Vengono Applicati gli Sconti

Quando invia sconti ad Airbnb, i-Rent applica le seguenti regole:

### Impostazioni Data
- **Date di inizio/fine pubblicazione** — Se impostate, lo sconto appare solo durante quel periodo
- **Intervallo date di arrivo** — Se specificato, lo sconto si applica alle prenotazioni che arrivano entro quelle date; se lasciato vuoto, lo sconto si applica a tutti i giorni con prezzi

### Requisiti di Soggiorno
- **Soggiorno minimo** — Se imposti un soggiorno minimo, lo sconto si applica solo quando è uguale o inferiore al requisito di soggiorno minimo dell'alloggio
- **Soggiorno massimo** — Non supportato per sconti Airbnb

### Metodo di Calcolo
- **Criteri di applicazione** — Lo sconto si applica se almeno un giorno della prenotazione cade nel periodo di sconto
- **Calcolo** — Lo sconto viene calcolato solo per i giorni che cadono nel periodo di sconto

## Vantaggi

- **Promozioni coerenti** — Gestisci tutti i tuoi sconti da un unico posto
- **Sincronizzazione automatica** — Non c'è bisogno di aggiornare Airbnb manualmente
- **Più prenotazioni** — Sconti attraenti aiutano a riempire il tuo calendario
- **Controllo totale** — Imposta esattamente quando e come si applicano gli sconti

## Inizia a Creare Sconti

Crea i tuoi sconti percentuali in i-Rent e guardali sincronizzarsi automaticamente con Airbnb. È un modo semplice per aumentare la tua visibilità e attirare più ospiti.
    `,

    // Article 13: accommodation-categories-rental-types
    'blog.article.rental-categories.title': 'Categorizza gli Alloggi per Tipo di Affitto',
    'blog.article.rental-categories.excerpt': 'Organizza il tuo portfolio distinguendo tra affitti vacanze, affitti a lungo termine e proprietà solo manutenzione — semplificando il tuo flusso di lavoro di pubblicazione.',
    'blog.article.rental-categories.content': `
## Migliore Organizzazione per le Tue Proprietà

Abbiamo aggiunto una nuova funzionalità che ti aiuta a organizzare i tuoi alloggi più efficacemente categorizzandoli in base al loro scopo.

## Tre Categorie di Proprietà

Ora puoi assegnare ogni alloggio a una delle tre categorie:

- **Affitti vacanze** — Proprietà disponibili per prenotazioni vacanze a breve termine
- **Affitti a lungo termine** — Proprietà destinate a soggiorni prolungati o affitti residenziali
- **Solo manutenzione e servizio** — Proprietà non disponibili per prenotazioni ospiti

## Come Configurare

Naviga al menu **Informazioni Alloggio** in i-Rent e seleziona la categoria appropriata per ogni proprietà. L'impostazione è rapida da configurare e entra in vigore immediatamente.

## Pubblicazione Più Intelligente

Il vero vantaggio arriva quando pubblichi su siti web e portali:

- **Le proprietà solo manutenzione sono automaticamente escluse** dalle liste di pubblicazione
- **Niente più annunci accidentali** di proprietà che non dovrebbero essere prenotabili
- **La pubblicazione in blocco diventa più sicura** — seleziona gruppi di alloggi senza preoccuparti di includere le proprietà sbagliate

## Vantaggi

- **Flusso di lavoro più pulito** — Concentrati solo sulle proprietà da pubblicare
- **Meno errori** — Impedisci che le proprietà di manutenzione appaiano sui siti di prenotazione
- **Migliore organizzazione** — Chiara separazione tra diversi tipi di affitto
- **Risparmio di tempo** — Non c'è bisogno di escludere manualmente le proprietà quando pubblichi

## Inizia a Organizzare

Prenditi qualche minuto per categorizzare i tuoi alloggi. Questo piccolo investimento di tempo renderà le tue operazioni quotidiane più fluide e ridurrà il rischio di errori di pubblicazione.
    `,

    // Article 14: holidu-online-payment-link
    'blog.article.holidu-payments.title': 'Link di Pagamento Online Ora Visualizzati su Holidu',
    'blog.article.holidu-payments.excerpt': 'Le prenotazioni Holidu ora includono link di pagamento automatici — gli ospiti vedono l\'opzione di pagamento direttamente sulla pagina di conferma e nella loro email.',
    'blog.article.holidu-payments.content': `
## Pagamenti Fluidi per le Prenotazioni Holidu

Abbiamo integrato link di pagamento automatici per le prenotazioni effettuate tramite Holidu. Questo semplifica il processo di pagamento e migliora l'esperienza dell'ospite.

## Come Funziona

Quando Holidu crea una prenotazione, i-Rent invia automaticamente un link di pagamento online. Questo link viene poi:

- **Visualizzato sulla pagina di conferma prenotazione Holidu** — gli ospiti possono pagare immediatamente dopo la prenotazione
- **Incluso nell'email di conferma** — facile accesso per gli ospiti che preferiscono pagare dopo

Il link di pagamento include l'importo dell'affitto più eventuali extra obbligatori da pagare al momento della prenotazione.

## Requisiti

Questa funzionalità è disponibile per i gestori di proprietà che hanno un **sistema POS (Point of Sale)** configurato in i-Rent. Se non hai ancora configurato il tuo sistema di pagamento, contatta il nostro team di supporto per assistenza.

## Vantaggi

- **Pagamenti più veloci** — Gli ospiti possono pagare immediatamente senza aspettare fatture manuali
- **Migliore conversione** — Ridurre l'attrito porta a più prenotazioni completate
- **Esperienza professionale** — Le pagine di pagamento brandizzate costruiscono fiducia con gli ospiti
- **Tracciamento automatico** — Tutti i pagamenti si sincronizzano direttamente con la tua dashboard i-Rent

## Inizia Ora

Se sei già connesso a Holidu con un sistema POS in i-Rent, questa funzionalità è attiva automaticamente. Inizia a goderti flussi di pagamento più fluidi per le tue prenotazioni Holidu oggi stesso.
    `,

    // Article 15: better-photos-holiday-homes
    'blog.article.photo-tips.title': 'Come Scattare Foto Migliori delle Case Vacanza',
    'blog.article.photo-tips.excerpt': 'Consigli fotografici professionali per affitti vacanze — dalle riprese esterne all\'allestimento degli interni, impara a catturare immagini che convertono i visitatori in prenotazioni.',
    'blog.article.photo-tips.content': `
## Perché le Belle Foto Contano

La fotografia di qualità è uno dei fattori più importanti per attirare prenotazioni. Questi consigli professionali ti aiuteranno a catturare immagini che mostrano le tue proprietà al meglio.

## Basi della Fotocamera

Prima di iniziare a scattare, tieni a mente questi fondamentali:

- **Scatta sempre in formato orizzontale** per evitare problemi di ritaglio su OTA e portali
- **Usa la massima risoluzione** per le immagini più nitide possibili
- **Mantieni la fotocamera in piano** (circa 1,5m da terra) per evitare pareti e porte storte
- **Usa un obiettivo grandangolare** per catturare più di ogni spazio
- **Scatta foto di giorno più 1-2 foto serali** subito dopo il tramonto quando il cielo è ancora blu

## Fotografia Esterna

### Linee Guida Generali

1. **Scatta solo in giornate soleggiate** — piccole nuvole bianche sono accettabili
2. **Posiziona il sole alle tue spalle** per cieli blu e illuminazione uniforme
3. **Apri tutte le persiane, porte e tende** visibili dall'esterno
4. **Minimizza gli edifici vicini** nell'inquadratura — evita di mostrare finestre delle case circostanti

### Giardino e Piscina

- Prepara l'area esattamente come gli ospiti la troverebbero all'arrivo
- Sistema lettini e mobili in modo ordinato e invitante
- Aggiungi asciugamani sui lettini e usa decorazioni di buon gusto
- Nascondi tubi da giardino e oggetti disordinati
- **Consiglio pro:** Mescola l'acqua della piscina con una scopa prima di scattare per nascondere eventuali detriti sul fondo

### Terrazze

- Mantieni tutto pulito e ordinato
- Apparecchia la tavola completamente con piatti, posate, vino e frutta
- Crea un'atmosfera che inviti a cenare

### Viste

- Minimizza gli edifici circostanti nell'inquadratura
- Scatta con il sole alle spalle
- Mostra che la vista appartiene a questa proprietà specifica

## Fotografia Interna

### Consigli Generali per Interni

- **Accendi TUTTE le luci** — ogni singola
- **Riordina accuratamente** e rimuovi il disordine
- **Scatta dagli angoli** con non più di 2 pareti visibili — questo crea una migliore sensazione spaziale
- **Riorganizza i mobili** per creare più spazio; evita di fotografare i retro dei divani
- **Nascondi cavi** e fili elettrici lungo le pareti
- **Liscia i cuscini** o rimuovili per composizioni più pulite
- Usa decorazioni, ma non esagerare

### Gestione della Luce

- Se entra troppa luce solare dalle finestre, chiudi le tende o copri le finestre dall'esterno
- Per scatti attraverso finestre verso un esterno luminoso, usa un treppiede e fai due esposizioni — una per la vista, una per l'interno — poi combinale in Photoshop

### Soggiorni e Sale da Pranzo

- Rimuovi tappeti, copridivani e cuscini in eccesso
- Rimuovi oggetti personali come foto di famiglia
- Apri finestre e porte quando la luce lo permette
- Cattura scatti di dettagli interessanti

### Camere da Letto

- Usa solo lenzuola o piumoni bianchi
- Aggiungi asciugamani arrotolati sul letto e più cuscini
- Rimuovi tutti gli oggetti personali
- Nelle stanze piccole, concentrati sul dettaglio del letto con i comodini

### Bagni

- Rimuovi tutti gli oggetti personali
- Se lo spazio è troppo stretto per l'orientamento orizzontale, scatta due foto in verticale e combinale affiancate

## Consigli di Post-Produzione

- Ritaglia gli elementi che distraggono
- Raddrizza pareti e porte nel tuo editor fotografico
- Schiarisci le immagini e nitidizza dove necessario
- Assicura una qualità costante su tutte le foto

## Inizia a Scattare

La grande fotografia non richiede attrezzature costose — solo attenzione ai dettagli e tecnica corretta. Applica questi consigli costantemente e guarda aumentare le tue richieste di prenotazione.
    `,
  },

  pt: {
    // Article 11: high-resolution-photos-portals
    'blog.article.photo-portals.title': 'Fotos em Alta Resolução e Tags Agora Enviadas para OTAs',
    'blog.article.photo-portals.excerpt': 'As fotos das suas propriedades são agora publicadas em alta resolução com tags descritivas no Booking.com, Airbnb e outros grandes portais.',
    'blog.article.photo-portals.content': `
## Melhores Fotos em Cada Plataforma

Melhorámos a forma como o i-Rent envia fotos para portais externos. As suas imagens de alta resolução são agora entregues em qualidade total, juntamente com tags descritivas que ajudam as plataformas a categorizar e exibir as suas fotos eficazmente.

## Tags de Foto por Plataforma

Diferentes portais suportam tagging de fotos de diferentes formas:

- **Booking.com** — Suporta múltiplas tags por foto
- **Airbnb** — Suporta uma tag por foto
- **HomeAway** — Suporta uma tag por foto com traduções em múltiplos idiomas

## Porque as Tags de Foto São Importantes

As tags de foto ajudam as plataformas de reserva a entender o que cada imagem mostra — seja um quarto, casa de banho, piscina ou vista. Isto melhora:

- **Visibilidade na pesquisa** — As plataformas podem combinar as suas fotos com as pesquisas dos hóspedes
- **Experiência do hóspede** — Os viajantes encontram o que procuram mais rapidamente
- **Qualidade do anúncio** — Fotos bem tagueadas sinalizam um anúncio profissional

## Alta Resolução em Todo o Lado

Juntamente com as tags, as suas fotos são agora enviadas em alta resolução para os portais que o suportam. Isto significa:

- **Imagens mais nítidas** — Sem mais artefactos de compressão
- **Melhores primeiras impressões** — Fotos de alta qualidade atraem mais cliques
- **Apresentação profissional** — As suas propriedades aparecem no seu melhor

## Comece Agora

Se ainda não experimentou o nosso sistema de fotos atualizado, agora é o momento perfeito. Aproveite os uploads em alta resolução e o tagging de fotos para destacar os seus anúncios em cada plataforma.
    `,

    // Article 12: discounts-sync-airbnb
    'blog.article.airbnb-discounts.title': 'Descontos Simples Agora Sincronizam com o Airbnb',
    'blog.article.airbnb-discounts.excerpt': 'Os seus descontos percentuais criados no i-Rent são agora automaticamente enviados para o Airbnb — ajudando-o a atrair mais reservas com promoções consistentes.',
    'blog.article.airbnb-discounts.content': `
## Descontos no Airbnb, Geridos a partir do i-Rent

Expandimos a nossa integração Airbnb para incluir sincronização de descontos. Os descontos simples que cria no i-Rent são agora automaticamente enviados para os seus anúncios Airbnb.

## Requisitos para Descontos Airbnb

Para um desconto sincronizar com o Airbnb, deve cumprir estas condições:

- **Baseado em percentagem** — Apenas descontos usando uma percentagem (%) são suportados
- **Sem estadia máxima** — O desconto não pode ter uma restrição de estadia máxima

## Como os Descontos São Aplicados

Ao enviar descontos para o Airbnb, o i-Rent aplica as seguintes regras:

### Configurações de Data
- **Datas de início/fim de publicação** — Se definidas, o desconto só aparece durante esse período
- **Intervalo de datas de chegada** — Se especificado, o desconto aplica-se a reservas que chegam dentro dessas datas; se deixado vazio, o desconto aplica-se a todos os dias com preços

### Requisitos de Estadia
- **Estadia mínima** — Se definir uma estadia mínima, o desconto só se aplica quando é igual ou inferior ao requisito de estadia mínima do alojamento
- **Estadia máxima** — Não suportado para descontos Airbnb

### Método de Cálculo
- **Critérios de aplicação** — O desconto aplica-se se pelo menos um dia da reserva cair no período de desconto
- **Cálculo** — O desconto é calculado apenas para os dias que caem no período de desconto

## Benefícios

- **Promoções consistentes** — Gira todos os seus descontos a partir de um único local
- **Sincronização automática** — Não precisa de atualizar o Airbnb manualmente
- **Mais reservas** — Descontos atrativos ajudam a preencher o seu calendário
- **Controlo total** — Defina exatamente quando e como os descontos se aplicam

## Comece a Criar Descontos

Crie os seus descontos percentuais no i-Rent e veja-os sincronizar automaticamente com o Airbnb. É uma forma simples de aumentar a sua visibilidade e atrair mais hóspedes.
    `,

    // Article 13: accommodation-categories-rental-types
    'blog.article.rental-categories.title': 'Categorize Alojamentos por Tipo de Aluguer',
    'blog.article.rental-categories.excerpt': 'Organize o seu portfólio distinguindo entre alugueres de férias, alugueres de longo prazo e propriedades apenas de manutenção — simplificando o seu fluxo de trabalho de publicação.',
    'blog.article.rental-categories.content': `
## Melhor Organização para as Suas Propriedades

Adicionámos uma nova funcionalidade que o ajuda a organizar os seus alojamentos de forma mais eficaz, categorizando-os com base no seu propósito.

## Três Categorias de Propriedades

Pode agora atribuir cada alojamento a uma de três categorias:

- **Alugueres de férias** — Propriedades disponíveis para reservas de férias de curto prazo
- **Alugueres de longo prazo** — Propriedades destinadas a estadias prolongadas ou arrendamentos residenciais
- **Apenas manutenção e serviço** — Propriedades não disponíveis para reservas de hóspedes

## Como Configurar

Navegue até ao menu **Informações do Alojamento** no i-Rent e selecione a categoria apropriada para cada propriedade. A configuração é rápida e entra em vigor imediatamente.

## Publicação Mais Inteligente

O verdadeiro benefício vem quando publica em websites e portais:

- **Propriedades apenas de manutenção são automaticamente excluídas** das listas de publicação
- **Sem mais anúncios acidentais** de propriedades que não deveriam estar reserváveis
- **A publicação em massa torna-se mais segura** — selecione grupos de alojamentos sem se preocupar em incluir as propriedades erradas

## Benefícios

- **Fluxo de trabalho mais limpo** — Concentre-se apenas nas propriedades que devem ser publicadas
- **Menos erros** — Evite que propriedades de manutenção apareçam em sites de reserva
- **Melhor organização** — Separação clara entre diferentes tipos de aluguer
- **Poupança de tempo** — Não precisa de excluir manualmente propriedades ao publicar

## Comece a Organizar

Reserve alguns minutos para categorizar os seus alojamentos. Este pequeno investimento de tempo tornará as suas operações diárias mais suaves e reduzirá o risco de erros de publicação.
    `,

    // Article 14: holidu-online-payment-link
    'blog.article.holidu-payments.title': 'Links de Pagamento Online Agora Exibidos no Holidu',
    'blog.article.holidu-payments.excerpt': 'As reservas Holidu agora incluem links de pagamento automáticos — os hóspedes veem a opção de pagamento diretamente na página de confirmação e no seu email.',
    'blog.article.holidu-payments.content': `
## Pagamentos Fluidos para Reservas Holidu

Integrámos links de pagamento automáticos para reservas feitas através do Holidu. Isto simplifica o processo de pagamento e melhora a experiência do hóspede.

## Como Funciona

Quando o Holidu cria uma reserva, o i-Rent envia automaticamente um link de pagamento online. Este link é então:

- **Exibido na página de confirmação de reserva do Holidu** — os hóspedes podem pagar imediatamente após reservar
- **Incluído no email de confirmação** — acesso fácil para hóspedes que preferem pagar mais tarde

O link de pagamento inclui o valor do aluguer mais quaisquer extras obrigatórios que precisem ser pagos no momento da reserva.

## Requisitos

Esta funcionalidade está disponível para gestores de propriedades que têm um **sistema POS (Ponto de Venda)** configurado no i-Rent. Se ainda não configurou o seu sistema de pagamento, contacte a nossa equipa de suporte para assistência.

## Benefícios

- **Pagamentos mais rápidos** — Os hóspedes podem pagar imediatamente sem esperar por faturas manuais
- **Melhor conversão** — Reduzir a fricção leva a mais reservas concluídas
- **Experiência profissional** — Páginas de pagamento com marca constroem confiança com os hóspedes
- **Rastreamento automático** — Todos os pagamentos sincronizam diretamente com o seu painel i-Rent

## Comece Agora

Se já está conectado ao Holidu com um sistema POS no i-Rent, esta funcionalidade está ativa automaticamente. Comece a desfrutar de fluxos de pagamento mais suaves para as suas reservas Holidu hoje mesmo.
    `,

    // Article 15: better-photos-holiday-homes
    'blog.article.photo-tips.title': 'Como Tirar Melhores Fotos de Casas de Férias',
    'blog.article.photo-tips.excerpt': 'Dicas de fotografia profissional para alugueres de férias — desde fotos exteriores até staging interior, aprenda a capturar imagens que convertem visitantes em reservas.',
    'blog.article.photo-tips.content': `
## Porque as Boas Fotos Importam

A fotografia de qualidade é um dos fatores mais importantes para atrair reservas. Estas dicas profissionais ajudá-lo-ão a capturar imagens que mostram as suas propriedades no seu melhor.

## Básicos da Câmara

Antes de começar a fotografar, tenha estes fundamentos em mente:

- **Fotografe sempre em formato paisagem** para evitar problemas de recorte em OTAs e portais
- **Use resolução máxima** para as imagens mais nítidas possíveis
- **Mantenha a câmara nivelada** (cerca de 1,5m do chão) para evitar paredes e portas inclinadas
- **Use uma lente grande angular** para capturar mais de cada espaço
- **Tire fotos de dia mais 1-2 fotos ao anoitecer** logo após o pôr do sol quando o céu ainda está azul

## Fotografia Exterior

### Diretrizes Gerais

1. **Fotografe apenas em dias ensolarados** — pequenas nuvens brancas são aceitáveis
2. **Posicione o sol atrás de si** para céus azuis e iluminação uniforme
3. **Abra todas as persianas, portas e cortinas** visíveis do exterior
4. **Minimize edifícios vizinhos** no seu enquadramento — evite mostrar janelas de casas circundantes

### Jardim e Piscina

- Prepare a área exatamente como os hóspedes a encontrariam à chegada
- Arranje espreguiçadeiras e mobiliário de forma ordenada e convidativa
- Adicione toalhas nas espreguiçadeiras e use decoração de bom gosto
- Esconda mangueiras de jardim e objetos desarrumados
- **Dica profissional:** Mexa a água da piscina com uma vassoura antes de fotografar para esconder quaisquer detritos no fundo

### Terraços

- Mantenha tudo limpo e arrumado
- Ponha a mesa completamente com pratos, talheres, vinho e fruta
- Crie uma atmosfera que convide a jantar

### Vistas

- Minimize edifícios circundantes no enquadramento
- Fotografe com o sol atrás de si
- Mostre que a vista pertence a esta propriedade específica

## Fotografia Interior

### Dicas Gerais de Interior

- **Ligue TODAS as luzes** — cada uma delas
- **Arrume cuidadosamente** e remova a desordem
- **Fotografe a partir dos cantos** com não mais de 2 paredes visíveis — isto cria uma melhor sensação espacial
- **Reorganize os móveis** para criar mais espaço; evite fotografar as costas dos sofás
- **Esconda cabos** e fios elétricos ao longo das paredes
- **Alise as almofadas** ou remova-as para composições mais limpas
- Use decoração, mas não exagere

### Gestão da Luz

- Se entra demasiada luz solar pelas janelas, feche cortinas ou cubra janelas do exterior
- Para fotos através de janelas para um exterior luminoso, use um tripé e faça duas exposições — uma para a vista, uma para o interior — depois combine no Photoshop

### Salas de Estar e Jantar

- Remova tapetes, capas de sofá e almofadas em excesso
- Remova objetos pessoais como fotos de família
- Abra janelas e portas quando a luz permitir
- Capture fotos de detalhes interessantes

### Quartos

- Use apenas lençóis ou edredons brancos
- Adicione toalhas enroladas na cama e múltiplas almofadas
- Remova todos os objetos pessoais
- Em quartos pequenos, concentre-se no detalhe da cama com mesas de cabeceira

### Casas de Banho

- Remova todos os objetos pessoais
- Se o espaço for demasiado apertado para orientação paisagem, tire duas fotos em retrato e combine-as lado a lado

## Dicas de Pós-Produção

- Recorte quaisquer elementos que distraiam
- Endireite paredes e portas no seu editor de fotos
- Ilumine imagens e nitidez onde necessário
- Assegure qualidade consistente em todas as fotos

## Comece a Fotografar

A boa fotografia não requer equipamento caro — apenas atenção aos detalhes e técnica adequada. Aplique estas dicas consistentemente e veja os seus pedidos de reserva aumentarem.
    `,
  },

  dk: {
    // Article 11: high-resolution-photos-portals
    'blog.article.photo-portals.title': 'Højopløsningsbilleder og Tags Sendes Nu til OTA\'er',
    'blog.article.photo-portals.excerpt': 'Dine ejendomsbilleder publiceres nu i høj opløsning med beskrivende tags på Booking.com, Airbnb og andre store portaler.',
    'blog.article.photo-portals.content': `
## Bedre Billeder på Hver Platform

Vi har opgraderet hvordan i-Rent sender billeder til eksterne portaler. Dine højopløsningsbilleder leveres nu i fuld kvalitet, sammen med beskrivende tags der hjælper platforme med at kategorisere og vise dine billeder effektivt.

## Foto Tags efter Platform

Forskellige portaler understøtter foto-tagging på forskellige måder:

- **Booking.com** — Understøtter flere tags per billede
- **Airbnb** — Understøtter én tag per billede
- **HomeAway** — Understøtter én tag per billede med oversættelser til flere sprog

## Hvorfor Foto Tags Er Vigtige

Foto tags hjælper bookingplatforme med at forstå hvad hvert billede viser — om det er et soveværelse, badeværelse, pool eller udsigt. Dette forbedrer:

- **Søgesynlighed** — Platforme kan matche dine billeder med gæsters søgninger
- **Gæsteoplevelse** — Rejsende finder hurtigere hvad de leder efter
- **Annoncekvalitet** — Veltaggede billeder signalerer en professionel annonce

## Høj Opløsning Overalt

Sammen med tags sendes dine billeder nu i høj opløsning til portaler der understøtter det. Dette betyder:

- **Skarpere billeder** — Ingen flere kompressionsartefakter
- **Bedre førstehåndsindtryk** — Høj kvalitetsbilleder tiltrækker flere klik
- **Professionel præsentation** — Dine ejendomme ser bedst ud

## Kom i Gang

Hvis du ikke har prøvet vores opdaterede fotosystem endnu, er nu det perfekte tidspunkt. Udnyt højopløsningsuploads og foto-tagging for at få dine annoncer til at skille sig ud på hver platform.
    `,

    // Article 12: discounts-sync-airbnb
    'blog.article.airbnb-discounts.title': 'Simple Rabatter Synkroniserer Nu med Airbnb',
    'blog.article.airbnb-discounts.excerpt': 'Dine procentbaserede rabatter oprettet i i-Rent sendes nu automatisk til Airbnb — og hjælper dig med at tiltrække flere bookinger med konsistente kampagner.',
    'blog.article.airbnb-discounts.content': `
## Rabatter på Airbnb, Administreret fra i-Rent

Vi har udvidet vores Airbnb-integration til at inkludere rabatsynkronisering. Simple rabatter du opretter i i-Rent sendes nu automatisk til dine Airbnb-annoncer.

## Krav til Airbnb Rabatter

For at en rabat kan synkroniseres til Airbnb, skal den opfylde disse betingelser:

- **Procentbaseret** — Kun rabatter der bruger en procentdel (%) understøttes
- **Ingen maksimal opholdslængde** — Rabatten kan ikke have en begrænsning på maksimalt ophold

## Hvordan Rabatter Anvendes

Når rabatter sendes til Airbnb, anvender i-Rent følgende regler:

### Datoindstillinger
- **Start-/slutdato for publicering** — Hvis sat, vises rabatten kun i den periode
- **Ankomstdatointerval** — Hvis specificeret, gælder rabatten for bookinger der ankommer inden for disse datoer; hvis tom, gælder rabatten for alle dage med priser

### Opholdskrav
- **Minimum ophold** — Hvis du sætter et minimum ophold, gælder rabatten kun når det er lig med eller mindre end boligens minimumskrav
- **Maksimalt ophold** — Ikke understøttet for Airbnb-rabatter

### Beregningsmetode
- **Anvendelseskriterier** — Rabatten gælder hvis mindst én dag af bookingen falder inden for rabatperioden
- **Beregning** — Rabatten beregnes kun for dage der falder inden for rabatperioden

## Fordele

- **Konsistente kampagner** — Administrer alle dine rabatter fra ét sted
- **Automatisk synkronisering** — Ingen grund til manuelt at opdatere Airbnb
- **Flere bookinger** — Attraktive rabatter hjælper med at fylde din kalender
- **Fuld kontrol** — Indstil præcis hvornår og hvordan rabatter gælder

## Begynd at Oprette Rabatter

Opret dine procentbaserede rabatter i i-Rent og se dem synkronisere automatisk til Airbnb. Det er en simpel måde at øge din synlighed og tiltrække flere gæster.
    `,

    // Article 13: accommodation-categories-rental-types
    'blog.article.rental-categories.title': 'Kategoriser Boliger efter Udlejningstype',
    'blog.article.rental-categories.excerpt': 'Organiser din portefølje ved at skelne mellem ferieudlejning, langtidsudlejning og kun-vedligeholdelse ejendomme — strømlin din publiceringsworkflow.',
    'blog.article.rental-categories.content': `
## Bedre Organisation for Dine Ejendomme

Vi har tilføjet en ny funktion der hjælper dig med at organisere dine boliger mere effektivt ved at kategorisere dem baseret på deres formål.

## Tre Ejendomskategorier

Du kan nu tildele hver bolig til én af tre kategorier:

- **Ferieudlejning** — Ejendomme tilgængelige for kortvarige feriebookinger
- **Langtidsudlejning** — Ejendomme beregnet til forlængede ophold eller boliglejemål
- **Kun vedligeholdelse og service** — Ejendomme ikke tilgængelige for gæstebookinger

## Sådan Konfigurerer Du

Naviger til menuen **Boliginformation** i i-Rent og vælg den passende kategori for hver ejendom. Indstillingen er hurtig at konfigurere og træder i kraft med det samme.

## Smartere Publicering

Den virkelige fordel kommer ved publicering til websites og portaler:

- **Kun-vedligeholdelse ejendomme ekskluderes automatisk** fra publiceringslister
- **Ingen flere utilsigtede annoncer** af ejendomme der ikke burde være bookbare
- **Massepublicering bliver sikrere** — vælg grupper af boliger uden at bekymre dig om at inkludere de forkerte ejendomme

## Fordele

- **Renere workflow** — Fokuser kun på ejendomme der skal publiceres
- **Færre fejl** — Forhindrer at vedligeholdelsesejendomme vises på bookingsider
- **Bedre organisation** — Klar adskillelse mellem forskellige udlejningstyper
- **Tidsbesparelse** — Ingen grund til manuelt at ekskludere ejendomme ved publicering

## Begynd at Organisere

Tag et par minutter til at kategorisere dine boliger. Denne lille tidsinvestering vil gøre dine daglige operationer glattere og reducere risikoen for publiceringsfejl.
    `,

    // Article 14: holidu-online-payment-link
    'blog.article.holidu-payments.title': 'Online Betalingslinks Vises Nu på Holidu',
    'blog.article.holidu-payments.excerpt': 'Holidu-bookinger inkluderer nu automatiske betalingslinks — gæster ser betalingsmuligheden direkte på bekræftelsessiden og i deres email.',
    'blog.article.holidu-payments.content': `
## Problemfri Betalinger for Holidu Bookinger

Vi har integreret automatiske betalingslinks for bookinger foretaget gennem Holidu. Dette strømliner betalingsprocessen og forbedrer gæsteoplevelsen.

## Sådan Fungerer Det

Når Holidu opretter en booking, sender i-Rent automatisk et online betalingslink tilbage. Dette link vises så:

- **På Holidus bookingbekræftelsesside** — gæster kan betale med det samme efter booking
- **Inkluderet i bekræftelsesmailen** — nem adgang for gæster der foretrækker at betale senere

Betalingslinket inkluderer lejebeløbet plus eventuelle obligatoriske tillæg der skal betales ved booking.

## Krav

Denne funktion er tilgængelig for ejendomsadministratorer der har et **POS (Point of Sale) system** konfigureret i i-Rent. Hvis du ikke har sat dit betalingssystem op endnu, kontakt vores supportteam for hjælp.

## Fordele

- **Hurtigere betalinger** — Gæster kan betale med det samme uden at vente på manuelle fakturaer
- **Bedre konvertering** — Mindre friktion fører til flere gennemførte bookinger
- **Professionel oplevelse** — Brandede betalingssider opbygger tillid hos gæster
- **Automatisk sporing** — Alle betalinger synkroniseres direkte til dit i-Rent dashboard

## Kom i Gang

Hvis du allerede er forbundet til Holidu med et POS-system i i-Rent, er denne funktion automatisk aktiv. Begynd at nyde glattere betalingsflows for dine Holidu-bookinger i dag.
    `,

    // Article 15: better-photos-holiday-homes
    'blog.article.photo-tips.title': 'Sådan Tager Du Bedre Billeder af Feriehuse',
    'blog.article.photo-tips.excerpt': 'Professionelle fotograferingstips til ferieudlejning — fra udendørs optagelser til indendørs styling, lær at tage billeder der konverterer besøgende til bookinger.',
    'blog.article.photo-tips.content': `
## Hvorfor Gode Billeder Er Vigtige

Kvalitetsfotografering er en af de vigtigste faktorer for at tiltrække bookinger. Disse professionelle tips vil hjælpe dig med at tage billeder der viser dine ejendomme fra deres bedste side.

## Kamera Grundprincipper

Før du begynder at fotografere, husk disse grundprincipper:

- **Fotografer altid i landskabsformat** for at undgå beskæringsproblemer på OTA'er og portaler
- **Brug maksimal opløsning** for de skarpest mulige billeder
- **Hold kameraet vandret** (ca. 1,5m fra jorden) for at undgå skæve vægge og døre
- **Brug et vidvinkelobjektiv** for at fange mere af hvert rum
- **Tag dagbilleder plus 1-2 aftenbilleder** lige efter solnedgang når himlen stadig er blå

## Udendørs Fotografering

### Generelle Retningslinjer

1. **Fotografer kun på solskinsdage** — små hvide skyer er acceptable
2. **Placer solen bag dig** for blå himmel og jævn belysning
3. **Åbn alle persienner, døre og gardiner** synlige udefra
4. **Minimer nabobygninger** i dit billede — undgå at vise vinduer fra omkringliggende huse

### Have og Pool

- Forbered området præcis som gæster ville finde det ved ankomst
- Arranger liggestole og møbler på en ordentlig, indbydende måde
- Tilføj håndklæder på liggestole og brug smagfuld dekoration
- Skjul haveslanger og rodede ting
- **Pro tip:** Rør poolvandet med en kost før fotografering for at skjule snavs på bunden

### Terrasser

- Hold alt rent og ryddeligt
- Dæk bordet helt med tallerkener, bestik, vin og frugt
- Skab en atmosfære der inviterer til spisning

### Udsigter

- Minimer omkringliggende bygninger i billedet
- Fotografer med solen bag dig
- Vis at udsigten hører til denne specifikke ejendom

## Indendørs Fotografering

### Generelle Indendørs Tips

- **Tænd ALLE lys** — hvert eneste et
- **Ryd grundigt op** og fjern rod
- **Fotografer fra hjørner** med højst 2 vægge synlige — dette skaber bedre rumfornemmelse
- **Omorganiser møbler** for at skabe mere plads; undgå at fotografere bagsiden af sofaer
- **Skjul kabler** og elektriske ledninger langs vægge
- **Glat puder ud** eller fjern dem for renere kompositioner
- Brug dekoration, men overdriv ikke

### Lysstyring

- Hvis for meget sollys kommer ind gennem vinduer, luk gardiner eller dæk vinduer udefra
- For billeder gennem vinduer til et lyst udendørsområde, brug et stativ og tag to eksponeringer — én for udsigten, én for interiøret — kombiner dem derefter i Photoshop

### Stue og Spisestue

- Fjern tæpper, sofabetræk og overskydende puder
- Fjern personlige genstande som familiebilleder
- Åbn vinduer og døre når lyset tillader det
- Tag interessante detaljebilleder

### Soveværelser

- Brug kun hvide lagner eller dyner
- Tilføj rullede håndklæder på sengen og flere puder
- Fjern alle personlige genstande
- I små rum, fokuser på sengedetaljen med natborde

### Badeværelser

- Fjern alle personlige genstande
- Hvis pladsen er for trang til landskabsformat, tag to portrætbilleder og kombiner dem side om side

## Efterbehandlingstips

- Beskær forstyrrende elementer væk
- Ret vægge og døre op i dit billedredigeringsprogram
- Lys billeder op og skærp hvor nødvendigt
- Sørg for ensartet kvalitet på tværs af alle billeder

## Begynd at Fotografere

God fotografering kræver ikke dyrt udstyr — kun opmærksomhed på detaljer og korrekt teknik. Anvend disse tips konsekvent og se dine bookingforespørgsler stige.
    `,
  },
};

// Export slug mappings for batch 3
export const slugToTranslationKeyBatch3: Record<string, string> = {
  'high-resolution-photos-portals': 'blog.article.photo-portals',
  'discounts-sync-airbnb': 'blog.article.airbnb-discounts',
  'accommodation-categories-rental-types': 'blog.article.rental-categories',
  'holidu-online-payment-link': 'blog.article.holidu-payments',
  'better-photos-holiday-homes': 'blog.article.photo-tips',
};

export const translatedArticleSlugsBatch3 = [
  'high-resolution-photos-portals',
  'discounts-sync-airbnb',
  'accommodation-categories-rental-types',
  'holidu-online-payment-link',
  'better-photos-holiday-homes',
];
